import { useState, useEffect, useRef, useMemo } from "react";
import { MapContainer, TileLayer, Polyline, Marker, Popup, useMap } from "react-leaflet";
import { Map, Plane, Cloud, Clock, Loader2, AlertTriangle, Sun, CloudFog, CloudOff, X, HelpCircle, Copy, Check, Camera, FileText, MapPin, Navigation, Wind, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import axios from "axios";
import L from "leaflet";
import html2canvas from "html2canvas";
import "leaflet/dist/leaflet.css";
import { toast } from "sonner";
import { WEATHER_CRITERIA } from "../config/weatherDecisionCriteria";

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Helper function to convert decimal degrees to DMS format
const convertToDMS = (decimal, isLat) => {
  const absolute = Math.abs(decimal);
  const degrees = Math.floor(absolute);
  const minutesNotTruncated = (absolute - degrees) * 60;
  const minutes = Math.floor(minutesNotTruncated);
  const seconds = Math.round((minutesNotTruncated - minutes) * 60);
  
  const direction = isLat 
    ? (decimal >= 0 ? 'N' : 'S')
    : (decimal >= 0 ? 'E' : 'W');
  
  return `${degrees}°${minutes.toString().padStart(2, '0')}'${seconds.toString().padStart(2, '0')}"${direction}`;
};

// Format coordinates in DMS
const formatCoordsDMS = (lat, lon) => {
  return `${convertToDMS(lat, true)} ${convertToDMS(lon, false)}`;
};

// Create weather waypoint icon
const createWeatherWaypointIcon = (category, index, isOverWater) => {
  const bgColor = category === 'VFR' ? '#10b981' : category === 'MVFR' ? '#f59e0b' : '#ef4444';
  
  return L.divIcon({
    className: 'weather-waypoint-marker',
    html: `
      <div style="
        background: ${bgColor};
        border: 2px solid white;
        border-radius: 50%;
        width: 18px;
        height: 18px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 8px;
        font-weight: bold;
        color: white;
        box-shadow: 0 1px 3px rgba(0,0,0,0.4);
      ">
        ${index + 1}
      </div>
    `,
    iconSize: [18, 18],
    iconAnchor: [9, 9],
    popupAnchor: [0, -9]
  });
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Default SPARROW 3 Route - FL JO FRANCE KML coordinates (fallback)
const DEFAULT_SPARROW3_ROUTE = {
  name: "FL JO FRANCE",
  description: "Flight Line JO France - 120 NM",
  coordinates: [
    [51.19993399999999, 2.875865],
    [51.084722, 2.55],
    [51.0375, 2.384722],
    [50.97, 2.169722],
    [50.91916700000001, 1.791389],
    [50.848056, 1.644444],
    [50.784444, 1.655278],
    [50.71277800000001, 1.638056],
    [50.478611, 1.633333],
    [50.249444, 1.605833],
    [50.271111, 1.546111],
    [50.558611, 1.588889],
    [50.69277799999999, 1.580278],
    [50.769444, 1.614722],
    [50.855278, 1.602222],
    [50.946944, 1.795833],
    [50.978611, 1.969722],
    [50.965278, 2.155278],
    [51.018889, 2.278056],
    [51.081111, 2.551944],
    [51.199593, 2.872677]
  ],
  // Nearby airports along the route
  airports: [
    { icao: "EBOS", name: "Ostend-Bruges", lat: 51.1989, lon: 2.8622 },
    { icao: "LFAC", name: "Calais-Dunkerque", lat: 50.9617, lon: 1.9547 },
    { icao: "LFAT", name: "Le Touquet", lat: 50.5175, lon: 1.6306 }
  ]
};

// Custom airport marker icon
const createAirportIcon = (flightCategory) => {
  const color = flightCategory === 'VFR' ? '#10b981' : 
                flightCategory === 'MVFR' ? '#3b82f6' : 
                flightCategory === 'IFR' ? '#ef4444' : 
                flightCategory === 'LIFR' ? '#8b5cf6' : '#64748b';
  
  return L.divIcon({
    className: 'custom-airport-marker',
    html: `<div style="background-color: ${color}; width: 24px; height: 24px; border-radius: 50%; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3); display: flex; align-items: center; justify-content: center;">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
              <path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"/>
            </svg>
          </div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
    popupAnchor: [0, -12]
  });
};

// Map bounds fitter component
const FitBounds = ({ coordinates }) => {
  const map = useMap();
  useEffect(() => {
    if (coordinates && coordinates.length > 0) {
      const bounds = L.latLngBounds(coordinates);
      map.fitBounds(bounds, { padding: [30, 30] });
    }
  }, [coordinates, map]);
  return null;
};

// Map invalidation component - handles resize when DOM changes
const MapInvalidator = () => {
  const map = useMap();
  useEffect(() => {
    // Invalidate map size when component mounts or updates
    const timeoutId = setTimeout(() => {
      map.invalidateSize();
    }, 100);
    return () => clearTimeout(timeoutId);
  }, [map]);
  return null;
};

// Calculate which airports belong to which routes based on geographic proximity
// Returns a map of routeIndex -> array of airport ICAOs
const calculateRouteAirportAssociations = (routes, airports) => {
  if (!routes || routes.length === 0 || !airports || airports.length === 0) {
    return {};
  }

  // Helper: calculate distance between two points (Haversine formula simplified)
  const getDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Earth's radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  // Helper: find minimum distance from airport to any point on a route
  const getMinDistanceToRoute = (airport, routeCoords) => {
    if (!routeCoords || routeCoords.length === 0) return Infinity;
    let minDist = Infinity;
    for (const coord of routeCoords) {
      const dist = getDistance(airport.lat, airport.lon, coord[0], coord[1]);
      if (dist < minDist) minDist = dist;
    }
    return minDist;
  };

  const routeAirports = {};
  const PROXIMITY_THRESHOLD_KM = 50; // Airport must be within 50km of route

  // For each airport, assign it to ALL routes it's within proximity of
  // Linked and alternate airports are always included in all routes
  for (const airport of airports) {
    const isEssential = airport.type === 'linked' || airport.type === 'alternate';
    
    for (let i = 0; i < routes.length; i++) {
      const dist = getMinDistanceToRoute(airport, routes[i].coordinates);
      if (isEssential || dist < PROXIMITY_THRESHOLD_KM) {
        if (!routeAirports[i]) routeAirports[i] = [];
        if (!routeAirports[i].includes(airport.icao)) {
          routeAirports[i].push(airport.icao);
        }
      }
    }
  }

  return routeAirports;
};

const FlightCategoryBadge = ({ category }) => {
  const styles = {
    VFR: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    MVFR: 'bg-blue-100 text-blue-700 border-blue-200',
    IFR: 'bg-red-100 text-red-700 border-red-200',
    LIFR: 'bg-purple-100 text-purple-700 border-purple-200'
  };
  
  const icons = {
    VFR: <Sun className="w-3 h-3" />,
    MVFR: <Cloud className="w-3 h-3" />,
    IFR: <CloudFog className="w-3 h-3" />,
    LIFR: <CloudOff className="w-3 h-3" />
  };
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold border ${styles[category] || 'bg-slate-100 text-slate-700'}`}>
      {icons[category]}
      {category}
    </span>
  );
};

// ========================================
// WWmapsGifs - DISABLED FOR LATER REFERENCE
// Original Windy Maps GIF Grid Component - 2x3 grid of weather layers
// ========================================
/*
const WWmapsGifs = ({ lat, lon, markerLat, markerLon, onVisibilityChange, onGifsChange, savedGifs, savedVisible }) => {
  const [windyGifs, setWindyGifs] = useState(savedGifs || {});
  const [loadingLayers, setLoadingLayers] = useState({});
  const [showWindyMaps, setShowWindyMaps] = useState(savedVisible || false);
  
  // Initialize from saved data
  useEffect(() => {
    if (savedGifs && Object.keys(savedGifs).length > 0) {
      setWindyGifs(savedGifs);
      setShowWindyMaps(true);
    }
  }, [savedGifs]);
  
  // Define the 6 layers for the 2x3 grid
  const WINDY_LAYERS = [
    { id: 'wind', name: 'Wind', icon: '💨', windyLayer: 'wind' },
    { id: 'visibility', name: 'Visibility', icon: '👁️', windyLayer: 'visibility' },
    { id: 'rain', name: 'Rain', icon: '🌧️', windyLayer: 'rain' },
    { id: 'lclouds', name: 'Low Clouds', icon: '☁️', windyLayer: 'lclouds' },
    { id: 'icing', name: 'Icing', icon: '🧊', windyLayer: 'icing' },
    { id: 'waves', name: 'Waves', icon: '🌊', windyLayer: 'waves' }
  ];
  
  // ... rest of WWmapsGifs component ...
};
*/

// ========================================
// NEW: Windy Weather Maps Panel using iframe
// ========================================
const WindyMapsPanel = ({ lat, lon, onVisibilityChange, onSnapshotCapture, savedSnapshots }) => {
  const [showWindyMaps, setShowWindyMaps] = useState(false);
  const [selectedLayer, setSelectedLayer] = useState('radar');
  const [snapshots, setSnapshots] = useState(savedSnapshots || []);
  const [capturingSnapshot, setCapturingSnapshot] = useState(false);
  
  // Restore saved snapshots when loading from library
  useEffect(() => {
    if (savedSnapshots && savedSnapshots.length > 0) {
      setSnapshots(savedSnapshots);
      setShowWindyMaps(true); // Show the panel if there are saved snapshots
    }
  }, [savedSnapshots]);
  
  // Available layers for the dropdown - ordered per user request
  // Note: Windy embed only supports certain overlays. Waves/swell not fully supported and may show wind instead.
  const WINDY_LAYERS = [
    { id: 'radar', name: 'Weather Radar', icon: '📡' },
    { id: 'wind', name: 'Wind', icon: '💨' },
    { id: 'gust', name: 'Wind Gusts', icon: '🌬️' },
    { id: 'visibility', name: 'Visibility', icon: '👁️' },
    { id: 'lclouds', name: 'Low Clouds', icon: '☁️' },
    { id: 'rain', name: 'Rain', icon: '🌧️' },
    { id: 'temp', name: 'Temperature', icon: '🌡️' },
  ];
  
  // Notify parent when visibility changes
  useEffect(() => {
    if (onVisibilityChange) {
      onVisibilityChange(showWindyMaps);
    }
  }, [showWindyMaps, onVisibilityChange]);
  
  // Build iframe URL - use embed format
  const getWindyIframeUrl = () => {
    // Windy embed URL format
    return `https://embed.windy.com/embed2.html?lat=${lat}&lon=${lon}&detailLat=${lat}&detailLon=${lon}&width=650&height=450&zoom=8&level=surface&overlay=${selectedLayer}&product=ecmwf&menu=&message=true&marker=&calendar=now&pressure=&type=map&location=coordinates&detail=&metricWind=kt&metricTemp=%C2%B0C&radarRange=-1`;
  };
  
  // Capture snapshot for PDF - now adds to gallery
  const captureSnapshot = async () => {
    setCapturingSnapshot(true);
    try {
      const response = await axios.get(`${API}/weather/windy-snapshot`, {
        params: {
          lat,
          lon,
          layer: selectedLayer,
          zoom: 8
        }
      });
      
      if (response.data && response.data.snapshot) {
        const snapshotData = {
          id: Date.now(), // Unique ID for removal
          data: `data:image/jpeg;base64,${response.data.snapshot}`,
          layer: selectedLayer,
          layerName: WINDY_LAYERS.find(l => l.id === selectedLayer)?.name || selectedLayer,
          layerIcon: WINDY_LAYERS.find(l => l.id === selectedLayer)?.icon || '📸',
          sizeKb: response.data.size_kb
          // Note: UTC timestamp removed per user request
        };
        
        const newSnapshots = [...snapshots, snapshotData];
        setSnapshots(newSnapshots);
        
        // Notify parent component for PDF inclusion (send all snapshots)
        if (onSnapshotCapture) {
          onSnapshotCapture(newSnapshots);
        }
        
        toast.success(`${snapshotData.layerName} snapshot added (${response.data.size_kb}KB)`);
      }
    } catch (err) {
      console.error('Failed to capture snapshot:', err);
      toast.error('Failed to capture snapshot. Please try again.');
    } finally {
      setCapturingSnapshot(false);
    }
  };
  
  // Remove a snapshot from gallery
  const removeSnapshot = (snapshotId) => {
    const newSnapshots = snapshots.filter(s => s.id !== snapshotId);
    setSnapshots(newSnapshots);
    
    // Notify parent component
    if (onSnapshotCapture) {
      onSnapshotCapture(newSnapshots.length > 0 ? newSnapshots : null);
    }
    
    toast.success('Snapshot removed');
  };
  
  // Clear all snapshots
  const clearAllSnapshots = () => {
    setSnapshots([]);
    if (onSnapshotCapture) {
      onSnapshotCapture(null);
    }
    toast.success('All snapshots cleared');
  };
  
  return (
    <div className="p-4 rounded-lg border bg-cyan-50 border-cyan-200 w-full">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-lg">🗺️</span>
          <p className="text-xs font-bold text-cyan-700 uppercase">Windy Weather Maps</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowWindyMaps(!showWindyMaps)}
          className="text-xs"
        >
          {showWindyMaps ? 'Hide Map' : 'Show Weather Map'}
        </Button>
      </div>
      
      {showWindyMaps && (
        <div className="mt-3">
          {/* Layer selector */}
          <div className="flex flex-wrap gap-2 mb-3">
            {WINDY_LAYERS.map((layer) => (
              <button
                key={layer.id}
                onClick={() => setSelectedLayer(layer.id)}
                className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                  selectedLayer === layer.id
                    ? 'bg-cyan-600 text-white border-cyan-600'
                    : 'bg-white text-cyan-700 border-cyan-300 hover:bg-cyan-100'
                }`}
              >
                {layer.icon} {layer.name}
              </button>
            ))}
          </div>
          
          {/* Full width iframe container */}
          <div className="w-full rounded-lg overflow-hidden border border-cyan-200 bg-white">
            <iframe
              src={getWindyIframeUrl()}
              title="Windy Weather Map"
              className="w-full"
              style={{ height: '500px', border: 'none' }}
              allowFullScreen
            />
          </div>
          
          {/* Snapshot button and counter */}
          <div className="mt-3 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Button
                onClick={captureSnapshot}
                disabled={capturingSnapshot}
                className="bg-cyan-600 hover:bg-cyan-700 text-white text-xs px-4 py-2"
              >
                {capturingSnapshot ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Capturing...
                  </>
                ) : (
                  <>
                    <Camera className="w-4 h-4 mr-2" />
                    TAKE SNAPSHOT FOR WX REPORT
                  </>
                )}
              </Button>
              
              {snapshots.length > 0 && (
                <Button
                  onClick={clearAllSnapshots}
                  variant="outline"
                  className="text-red-600 border-red-300 hover:bg-red-50 text-xs px-3 py-2"
                >
                  <X className="w-3 h-3 mr-1" />
                  Clear All
                </Button>
              )}
            </div>
            
            {snapshots.length > 0 && (
              <div className="flex items-center gap-2 text-xs text-emerald-600">
                <Check className="w-4 h-4" />
                <span>{snapshots.length} snapshot{snapshots.length > 1 ? 's' : ''} ready for report</span>
              </div>
            )}
          </div>
          
          {/* Snapshot Gallery */}
          {snapshots.length > 0 && (
            <div className="mt-3 p-3 bg-white rounded-lg border border-cyan-200">
              <p className="text-[10px] text-slate-500 mb-3 font-medium">📸 Captured snapshots (will be included in WX report):</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {snapshots.map((snap) => (
                  <div key={snap.id} className="relative group">
                    <div className="rounded-lg border border-slate-200 overflow-hidden bg-slate-50">
                      <img 
                        src={snap.data} 
                        alt={`${snap.layerName} snapshot`}
                        className="w-full h-24 object-cover"
                      />
                      <div className="p-1.5 text-center">
                        <p className="text-[9px] font-medium text-slate-700 truncate">
                          {snap.layerIcon} {snap.layerName}
                        </p>
                        <p className="text-[8px] text-slate-500 dark:text-slate-400">{snap.sizeKb}KB</p>
                      </div>
                    </div>
                    {/* Remove button */}
                    <button
                      onClick={() => removeSnapshot(snap.id)}
                      className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-md"
                      title="Remove snapshot"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          <p className="text-xs text-slate-500 mt-3 text-left">
            Interactive weather map powered by Windy.com. Select different layers above to view various weather data.
          </p>
        </div>
      )}
      
      {!showWindyMaps && (
        <p className="text-xs text-slate-500">
          Click &quot;Show Weather Map&quot; to view interactive weather maps with radar, wind, rain, clouds, and more.
        </p>
      )}
    </div>
  );
};

export const FlightRouteWeather = ({ ftxDesignator, onMapCapture, onPredictionVisibilityChange, onWeatherPredictionChange, onWindyMapsChange, onWindySnapshotChange, aiWeatherBriefing, savedWindyMaps, savedWindySnapshots, skylegsMissionTiming, missionDate }) => {
  const [loading, setLoading] = useState(false);
  const [airportWeather, setAirportWeather] = useState([]);
  const [weatherPrediction, setWeatherPrediction] = useState(null);
  const [showPrediction, setShowPrediction] = useState(true);
  const [error, setError] = useState(null);
  const [mapReady, setMapReady] = useState(false);
  const [hiddenRouteAirports, setHiddenRouteAirports] = useState([]);
  const [showAiHelpDialog, setShowAiHelpDialog] = useState(false);
  const [windyMapsVisible, setWindyMapsVisible] = useState(savedWindyMaps?.visible || false);
  const [windyGifsData, setWindyGifsData] = useState(savedWindyMaps?.gifs || {});
  // Dynamic FTX data from backend
  const [ftxData, setFtxData] = useState(null);
  const [ftxLoading, setFtxLoading] = useState(false);
  // Route waypoint weather (including over water/sea)
  const [routeWaypointWeather, setRouteWaypointWeather] = useState(null);
  const [routeWaypointLoading, setRouteWaypointLoading] = useState(false);
  // Turbulence EDR data - disabled (always returned zero, removed from UI)
  // const [turbulenceData, setTurbulenceData] = useState(null);
  // const [turbulenceLoading, setTurbulenceLoading] = useState(false);
  // Selected routes for display (when multiple routes available)
  const [selectedRouteIndices, setSelectedRouteIndices] = useState([]);
  // Route-to-airport associations (calculated from proximity)
  const [routeAirportMap, setRouteAirportMap] = useState({});
  // Mission time selection (30-min intervals) - can be overridden by SkyLegs data
  const [missionStartTime, setMissionStartTime] = useState("16:00");
  const [missionEndTime, setMissionEndTime] = useState("20:00");
  const mapContainerRef = useRef(null);

  // Update mission times when SkyLegs data is received
  useEffect(() => {
    if (skylegsMissionTiming) {
      // Parse the timing string (e.g., "08:30Z - 12:00Z")
      const match = skylegsMissionTiming.match(/(\d{2}:\d{2})Z?\s*-\s*(\d{2}:\d{2})Z?/);
      if (match) {
        setMissionStartTime(match[1]);
        setMissionEndTime(match[2]);
      }
    }
  }, [skylegsMissionTiming]);

  // Generate time options in 30-minute intervals (00:00 to 23:30)
  const timeOptions = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let min = 0; min < 60; min += 30) {
      const timeStr = `${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`;
      timeOptions.push(timeStr);
    }
  }

  // Initialize from saved data if available
  useEffect(() => {
    if (savedWindyMaps?.gifs && Object.keys(savedWindyMaps.gifs).length > 0) {
      setWindyGifsData(savedWindyMaps.gifs);
      setWindyMapsVisible(true);
    }
  }, [savedWindyMaps]);

  // Track which airport's weather was copied
  const [copiedAirport, setCopiedAirport] = useState(null);

  // Fetch FTX designator data when ftxDesignator changes
  useEffect(() => {
    if (!ftxDesignator || ftxDesignator === "NONE") {
      setFtxData(null);
      return;
    }

    const fetchFtxData = async () => {
      setFtxLoading(true);
      try {
        const response = await axios.get(`${API}/admin/ftx/${encodeURIComponent(ftxDesignator)}`);
        const data = response.data;
        
        // If it's an object with airports, use it; otherwise use default for SPARROW 3
        if (data && data.airports && data.airports.length > 0) {
          // Handle multiple routes from the new API format
          let allRoutes = [];
          
          if (data.routes && data.routes.length > 0) {
            // New format: array of {name, coordinates} objects
            allRoutes = data.routes.map(route => ({
              name: route.name,
              coordinates: route.coordinates || []
            }));
          } else if (data.routeCoordinates && data.routeCoordinates.length > 0) {
            // Legacy format: single routeCoordinates array
            allRoutes = [{
              name: data.routeFile || "Route",
              coordinates: data.routeCoordinates
            }];
          } else {
            // Fallback: generate simple line from airports
            allRoutes = [{
              name: "Route",
              coordinates: data.airports.map(a => [a.lat, a.lon])
            }];
          }
          
          // Combine all coordinates for the main route display
          const allCoordinates = allRoutes.flatMap(r => r.coordinates);
          
          // Calculate which airports belong to which routes based on proximity
          const airportAssociations = calculateRouteAirportAssociations(allRoutes, data.airports);
          setRouteAirportMap(airportAssociations);
          
          setFtxData({
            name: data.name,
            description: data.description || "",
            airports: data.airports,
            routeFile: data.routeFile,
            routeFiles: data.routeFiles || [],
            routes: allRoutes,  // Array of routes for multi-line display
            coordinates: allCoordinates  // Combined coordinates for backward compatibility
          });
          
          // Initialize selected routes - default to all routes selected
          setSelectedRouteIndices(allRoutes.map((_, i) => i));
        } else if (ftxDesignator === "SPARROW 3") {
          // Fallback to default SPARROW 3 for backwards compatibility
          setFtxData(DEFAULT_SPARROW3_ROUTE);
          setSelectedRouteIndices([0]);
          setRouteAirportMap({ 0: DEFAULT_SPARROW3_ROUTE.airports.map(a => a.icao) });
        } else {
          setFtxData(null);
          setSelectedRouteIndices([]);
          setRouteAirportMap({});
        }
      } catch (error) {
        console.error("Failed to fetch FTX data:", error);
        // Fallback to default SPARROW 3 for backwards compatibility
        if (ftxDesignator === "SPARROW 3") {
          setFtxData(DEFAULT_SPARROW3_ROUTE);
        } else {
          setFtxData(null);
        }
      } finally {
        setFtxLoading(false);
      }
    };

    fetchFtxData();
  }, [ftxDesignator]);

  // Fetch route waypoint weather (including over water/sea) when FTX or selected routes change
  useEffect(() => {
    const fetchRouteWaypointWeather = async () => {
      if (!ftxDesignator || ftxDesignator === "NONE") {
        setRouteWaypointWeather(null);
        return;
      }

      // Don't fetch if no routes are selected
      if (selectedRouteIndices.length === 0) {
        setRouteWaypointWeather(null);
        return;
      }

      // Get coordinates from selected routes only
      let selectedCoordinates = [];
      const routeData = ftxData || DEFAULT_SPARROW3_ROUTE;
      if (routeData?.routes && routeData.routes.length > 0) {
        for (const idx of selectedRouteIndices) {
          if (routeData.routes[idx]?.coordinates) {
            selectedCoordinates = selectedCoordinates.concat(routeData.routes[idx].coordinates);
          }
        }
      }

      setRouteWaypointLoading(true);
      try {
        const requestData = {
          ftx_designator: ftxDesignator,
          num_waypoints: 6,
          check_radius_nm: 10,  // Check weather in 10nm radius
          show_coordinates: false,  // Don't show coordinates for waypoints
          selected_route_indices: selectedRouteIndices  // Pass selected routes
        };

        // If we have specific coordinates from selected routes, use them
        if (selectedCoordinates.length > 0) {
          requestData.coordinates = selectedCoordinates;
        }

        const response = await axios.post(`${API}/weather/route-waypoints`, requestData);
        
        if (response.data && response.data.waypoints?.length > 0) {
          setRouteWaypointWeather(response.data);
          console.log("Route waypoint weather loaded for selected routes:", selectedRouteIndices, response.data.route_assessment);
        } else {
          setRouteWaypointWeather(null);
        }
      } catch (error) {
        console.error("Failed to fetch route waypoint weather:", error);
        setRouteWaypointWeather(null);
      } finally {
        setRouteWaypointLoading(false);
      }
    };

    // Debounce the fetch to avoid too many requests when toggling routes
    const timeoutId = setTimeout(fetchRouteWaypointWeather, 300);
    return () => clearTimeout(timeoutId);
  }, [ftxDesignator, selectedRouteIndices, ftxData]);

  // Turbulence EDR fetch - disabled (always returned zero, removed from UI)
  /*
  useEffect(() => {
    const fetchTurbulenceEDR = async () => {
      if (!routeWaypointWeather?.waypoints?.length) {
        setTurbulenceData(null);
        return;
      }

      // Extract waypoints with coordinates for turbulence check
      const waypoints = routeWaypointWeather.waypoints
        .filter(wp => wp.lat != null && wp.lon != null)
        .map(wp => ({ lat: wp.lat, lon: wp.lon }));

      if (waypoints.length === 0) {
        setTurbulenceData(null);
        return;
      }

      setTurbulenceLoading(true);
      try {
        const response = await axios.post(`${API}/weather/turbulence-edr`, { waypoints });
        if (response.data && !response.data.error) {
          setTurbulenceData(response.data);
          console.log("Turbulence EDR loaded:", response.data.route_summary);
        } else {
          setTurbulenceData(null);
        }
      } catch (error) {
        console.error("Failed to fetch turbulence EDR:", error);
        setTurbulenceData(null);
      } finally {
        setTurbulenceLoading(false);
      }
    };

    // Small delay to batch with other weather fetches
    const timeoutId = setTimeout(fetchTurbulenceEDR, 500);
    return () => clearTimeout(timeoutId);
  }, [routeWaypointWeather]);
  */

  // Copy airport weather to clipboard
  const handleCopyWeather = async (airport) => {
    let text = `${airport.icao} - ${airport.name}\n`;
    if (airport.metar?.raw) {
      text += `\nMETAR:\n${airport.metar.raw}`;
    }
    if (airport.taf?.raw) {
      text += `\n\nTAF:\n${airport.taf.raw}`;
    }
    
    try {
      await navigator.clipboard.writeText(text);
      setCopiedAirport(airport.icao);
      setTimeout(() => setCopiedAirport(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Download raw weather analysis as TXT file
  const handleDownloadRawAnalysis = () => {
    if (!weatherPrediction?.summary) {
      toast.error('No analysis available to download');
      return;
    }
    
    // Build complete raw analysis text
    let rawText = weatherPrediction.summary;
    
    // Add timestamp and metadata
    const header = `================================================================================
RAW WEATHER ANALYSIS - ${ftxDesignator}
Generated: ${new Date().toISOString()}
================================================================================

`;
    
    rawText = header + rawText;
    
    // Create and download file
    const blob = new Blob([rawText], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const date = new Date().toISOString().split('T')[0];
    const time = new Date().toTimeString().slice(0, 5).replace(':', '');
    link.download = `WX_ANALYSIS_${ftxDesignator.replace(' ', '_')}_${date}_${time}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success('Raw analysis downloaded');
  };

  // Copy raw analysis to clipboard
  const handleCopyRawAnalysis = async () => {
    if (!weatherPrediction?.summary) {
      toast.error('No analysis available to copy');
      return;
    }
    
    try {
      await navigator.clipboard.writeText(weatherPrediction.summary);
      toast.success('Analysis copied to clipboard');
    } catch (err) {
      console.error('Failed to copy:', err);
      toast.error('Failed to copy to clipboard');
    }
  };

  // Active if we have a valid FTX with airports configured
  const isActive = ftxData && ftxData.airports && ftxData.airports.length > 0;
  const route = ftxData || DEFAULT_SPARROW3_ROUTE;

  // Calculate the center of the route coordinates for Windy map
  const routeCenter = useMemo(() => {
    if (!route?.coordinates || route.coordinates.length === 0) {
      // Fallback to first airport or default
      if (route?.airports?.[0]) {
        return { lat: route.airports[0].lat, lon: route.airports[0].lon };
      }
      return { lat: 50.5, lon: 2.5 }; // Default fallback
    }
    
    // Calculate centroid of all route coordinates
    let sumLat = 0;
    let sumLon = 0;
    const coords = route.coordinates;
    
    for (const coord of coords) {
      sumLat += coord[0]; // lat
      sumLon += coord[1]; // lon
    }
    
    return {
      lat: sumLat / coords.length,
      lon: sumLon / coords.length
    };
  }, [route]);

  // Filter airports based on selected routes (using routeAirportMap) and manually hidden airports
  const visibleAirportWeather = useMemo(() => {
    // First filter by manually hidden airports
    let filtered = airportWeather.filter(airport => !hiddenRouteAirports.includes(airport.icao));
    
    // Then filter by selected routes (only if we have multiple routes)
    // But always include "linked" and "alternate" type airports as they're important for mission planning
    if (route?.routes?.length > 1 && Object.keys(routeAirportMap).length > 0) {
      filtered = filtered.filter(airport => {
        // Always show linked airports (departure/arrival) and alternates
        if (airport.type === 'linked' || airport.type === 'alternate') {
          return true;
        }
        
        // Check if this airport belongs to any selected route
        for (const routeIdx of selectedRouteIndices) {
          const routeAirports = routeAirportMap[routeIdx] || [];
          if (routeAirports.includes(airport.icao)) {
            return true;
          }
        }
        return false;
      });
    }
    
    return filtered;
  }, [airportWeather, hiddenRouteAirports, route?.routes, selectedRouteIndices, routeAirportMap]);

  // Handler to hide an airport
  const handleHideRouteAirport = (icao) => {
    setHiddenRouteAirports(prev => [...prev, icao]);
  };

  // Notify parent when prediction visibility changes
  useEffect(() => {
    if (onPredictionVisibilityChange) {
      onPredictionVisibilityChange(showPrediction);
    }
  }, [showPrediction, onPredictionVisibilityChange]);

  // Notify parent when weather prediction data changes or when selected routes change
  useEffect(() => {
    if (onWeatherPredictionChange && weatherPrediction) {
      // Add selected route indices to the data passed to parent
      const dataWithSelectedRoutes = showPrediction ? {
        ...weatherPrediction,
        selectedRouteIndices: selectedRouteIndices
      } : null;
      onWeatherPredictionChange(dataWithSelectedRoutes);
    }
  }, [weatherPrediction, showPrediction, onWeatherPredictionChange, selectedRouteIndices]);

  // Notify parent when Windy maps visibility or data changes
  useEffect(() => {
    if (onWindyMapsChange) {
      onWindyMapsChange({
        visible: windyMapsVisible,
        gifs: windyGifsData
      });
    }
  }, [windyMapsVisible, windyGifsData, onWindyMapsChange]);

  // Reset prediction visibility when route changes
  useEffect(() => {
    if (isActive) {
      setShowPrediction(true);
      setHiddenRouteAirports([]);
    }
  }, [isActive, ftxDesignator]);

  // Capture map as image when ready
  useEffect(() => {
    if (!mapReady || !mapContainerRef.current || !onMapCapture) return;
    
    const captureMap = async () => {
      // Wait a bit for tiles to load
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      try {
        const canvas = await html2canvas(mapContainerRef.current, {
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff'
        });
        const base64 = canvas.toDataURL('image/png');
        onMapCapture(base64);
      } catch (err) {
        console.error("Failed to capture map:", err);
      }
    };
    
    captureMap();
  }, [mapReady, onMapCapture]);

  useEffect(() => {
    if (!isActive || !ftxData) {
      setAirportWeather([]);
      setWeatherPrediction(null);
      setMapReady(false);
      return;
    }

    const fetchRouteWeather = async () => {
      setLoading(true);
      setError(null);
      // Clear previous data immediately when FTX changes
      setAirportWeather([]);
      setWeatherPrediction(null);

      try {
        // Fetch weather for all airports along the route (using ftxData.airports)
        const airportsToFetch = ftxData.airports || [];
        if (airportsToFetch.length === 0) {
          setError("No airports configured for this FTX designator");
          setLoading(false);
          return;
        }

        const weatherPromises = airportsToFetch.map(async (airport) => {
          const [metarRes, tafRes] = await Promise.all([
            axios.get(`${API}/weather/metar/${airport.icao}`).catch(() => null),
            axios.get(`${API}/weather/taf/${airport.icao}`).catch(() => null)
          ]);

          return {
            ...airport,
            metar: metarRes?.data || null,
            taf: tafRes?.data || null
          };
        });

        const results = await Promise.all(weatherPromises);
        setAirportWeather(results);

        // Generate weather prediction based on collected data
        generateWeatherPrediction(results, routeWaypointWeather);
      } catch (err) {
        console.error("Failed to fetch route weather:", err);
        setError("Failed to fetch weather data for route");
      } finally {
        setLoading(false);
      }
    };

    fetchRouteWeather();
  }, [isActive, ftxData]); // Re-fetch when FTX data changes

  // Recalculate weather prediction when mission times or selected routes change
  useEffect(() => {
    if (airportWeather.length > 0) {
      generateWeatherPrediction(airportWeather, routeWaypointWeather);
    }
  }, [missionStartTime, missionEndTime, selectedRouteIndices, routeAirportMap, missionDate, routeWaypointWeather]);

  const generateWeatherPrediction = (weatherData, currentRouteWaypointWeather) => {
    // Get departure airport (first in the list, or first linked airport)
    const DEPARTURE_AIRPORT = route?.airports?.[0]?.icao || ftxData?.airports?.[0]?.icao || "EBOS";
    
    // Get all linked airports that should always be shown
    const linkedAirports = (route?.airports || ftxData?.airports || [])
      .filter(a => a.type === 'linked' || a.type === 'departure')
      .map(a => a.icao);
    
    // Filter weather data based on selected routes (if multiple routes exist)
    // But ALWAYS include departure airport and linked airports
    let filteredWeatherData = weatherData;
    if (route?.routes?.length > 1 && Object.keys(routeAirportMap).length > 0) {
      filteredWeatherData = weatherData.filter(airport => {
        // Always include departure and linked airports
        if (airport.icao === DEPARTURE_AIRPORT || linkedAirports.includes(airport.icao)) {
          return true;
        }
        // Check if airport is associated with any selected route
        for (const routeIdx of selectedRouteIndices) {
          const routeAirports = routeAirportMap[routeIdx] || [];
          if (routeAirports.includes(airport.icao)) {
            return true;
          }
        }
        return false;
      });
    }
    
    // Mission parameters - use dynamic times from state
    const startParts = missionStartTime.split(':');
    const endParts = missionEndTime.split(':');
    const MISSION_START_HOUR = parseInt(startParts[0]);
    const MISSION_START_MINUTE = parseInt(startParts[1]);
    const MISSION_END_HOUR = parseInt(endParts[0]);
    const MISSION_END_MINUTE = parseInt(endParts[1]);
    const DEPARTURE_TIME = `${missionStartTime.replace(':', '')}Z`;
    const ARRIVAL_TIME = `${missionEndTime.replace(':', '')}Z`;
    
    // NSAS LIMITS - from shared config (config/weatherDecisionCriteria.js)
    const CROSSWIND_LIMIT = WEATHER_CRITERIA.aircraft.crosswindLimit; // kts
    const ICING_ALTITUDE_LOW = WEATHER_CRITERIA.clouds.icingCheckRange[0]; // feet
    const ICING_ALTITUDE_HIGH = WEATHER_CRITERIA.clouds.icingCheckRange[1]; // feet
    const REDUCED_VISIBILITY = WEATHER_CRITERIA.visibility.poorThreshold; // meters
    const LOW_CLOUD_LIMIT = WEATHER_CRITERIA.clouds.lowCeilingThreshold; // feet
    
    // EBOS runway orientation from config
    const RUNWAY_HEADING = WEATHER_CRITERIA.airport.runwayHeadings[0];
    
    // Approximate sunset times (winter ~16:30-17:30 UTC, summer ~20:00-21:00 UTC)
    const currentMonth = new Date().getMonth(); // 0-11
    const estimatedSunsetHour = currentMonth >= 4 && currentMonth <= 8 ? 20 : 17; // Summer vs Winter
    const isMissionAfterSunset = MISSION_END_HOUR > estimatedSunsetHour || (MISSION_START_HOUR >= estimatedSunsetHour);
    
    // Check if METAR should be used for GO/NO GO logic
    // METAR is only relevant if briefing is generated within validity window before mission start
    const now = new Date();
    const currentHourUTC = now.getUTCHours();
    const currentMinuteUTC = now.getUTCMinutes();
    const missionStartInMinutes = MISSION_START_HOUR * 60 + MISSION_START_MINUTE;
    const currentTimeInMinutes = currentHourUTC * 60 + currentMinuteUTC;
    const minutesUntilMissionStart = missionStartInMinutes - currentTimeInMinutes;
    const useMetarForDecision = minutesUntilMissionStart <= WEATHER_CRITERIA.metar.validityWindow && minutesUntilMissionStart >= -240; // Within validity window before to 4hrs after start
    
    // Get departure airport data
    const departureData = filteredWeatherData.find(a => a.icao === DEPARTURE_AIRPORT);
    
    // ========================================
    // HELPER: Extract TAF sections - includes both mission window and all available forecasts
    // ========================================
    const extractRelevantTafSections = (tafRaw, includeAll = false) => {
      if (!tafRaw) return [];
      
      // Split TAF into time-based sections
      // Look for patterns like FM1600, TEMPO 1600/2000, BECMG 1500/1700, PROB30 1600/1800
      const sections = [];
      
      // Check if any time period overlaps with mission window
      // Use the selected mission date instead of today
      const missionDay = missionDate 
        ? new Date(missionDate + 'T00:00:00Z').getUTCDate() 
        : new Date().getUTCDate();
      
      const isInMissionWindow = (startDay, startHour, endDay = null, endHour = null) => {
        // If no end specified, assume FM period lasting ~6 hours
        if (endDay === null) endDay = startDay;
        if (endHour === null) endHour = startHour + 6;
        
        // Convert to absolute hours from start of mission day for easier comparison
        // If end day is different (spans midnight), the forecast covers a longer period
        const missionStart = MISSION_START_HOUR + (MISSION_START_MINUTE / 60);
        const missionEnd = MISSION_END_HOUR + (MISSION_END_MINUTE / 60);
        
        // Calculate forecast start/end relative to mission day
        const forecastStartRelative = (startDay === missionDay) ? startHour : 
                                      (startDay < missionDay) ? startHour - 24 : startHour + 24;
        const forecastEndRelative = (endDay === missionDay) ? endHour :
                                    (endDay < missionDay) ? endHour - 24 : 
                                    (endDay > missionDay) ? endHour + 24 : endHour;
        
        // Check overlap: forecast overlaps mission if forecast doesn't end before mission starts
        // AND forecast doesn't start after mission ends
        return (forecastStartRelative < missionEnd && forecastEndRelative > missionStart);
      };
      
      // Extract base forecast (before any FM/TEMPO/BECMG)
      const baseMatch = tafRaw.match(/TAF[^]*?(\d{4}\/\d{4})\s+([^]*?)(?=FM|TEMPO|BECMG|PROB|$)/);
      if (baseMatch) {
        const validPeriod = baseMatch[1];
        const startDay = parseInt(validPeriod.substring(0, 2));
        const startHour = parseInt(validPeriod.substring(2, 4));
        const endDay = parseInt(validPeriod.substring(5, 7));
        const endHour = parseInt(validPeriod.substring(7, 9));
        const inWindow = isInMissionWindow(startDay, startHour, endDay, endHour);
        if (includeAll || inWindow) {
          sections.push({ type: 'BASE', content: baseMatch[2], start: startHour, end: endHour, startDay, endDay, inMissionWindow: inWindow });
        }
      }
      
      // Extract FM periods
      let fmMatch;
      const fmRegex = /FM(\d{2})(\d{2})\s+([^]*?)(?=FM\d{4}|TEMPO|BECMG|PROB\d{2}|$)/gi;
      while ((fmMatch = fmRegex.exec(tafRaw)) !== null) {
        const startDay = parseInt(fmMatch[1]);
        const startHour = parseInt(fmMatch[2]);
        const inWindow = isInMissionWindow(startDay, startHour);
        if (includeAll || inWindow) {
          sections.push({ type: 'FM', content: fmMatch[3], start: startHour, startDay, inMissionWindow: inWindow });
        }
      }
      
      // Extract TEMPO periods
      const tempoRegex = /TEMPO\s+(\d{2})(\d{2})\/(\d{2})(\d{2})\s+([^]*?)(?=FM|TEMPO|BECMG|PROB|$)/gi;
      let tempoMatch;
      while ((tempoMatch = tempoRegex.exec(tafRaw)) !== null) {
        const startDay = parseInt(tempoMatch[1]);
        const startHour = parseInt(tempoMatch[2]);
        const endDay = parseInt(tempoMatch[3]);
        const endHour = parseInt(tempoMatch[4]);
        const inWindow = isInMissionWindow(startDay, startHour, endDay, endHour);
        if (includeAll || inWindow) {
          sections.push({ type: 'TEMPO', content: tempoMatch[5], start: startHour, end: endHour, startDay, endDay, inMissionWindow: inWindow });
        }
      }
      
      // Extract PROB periods
      const probRegex = /PROB(\d{2})\s+(\d{2})(\d{2})\/(\d{2})(\d{2})\s+([^]*?)(?=FM|TEMPO|BECMG|PROB|$)/gi;
      let probMatch;
      while ((probMatch = probRegex.exec(tafRaw)) !== null) {
        const prob = parseInt(probMatch[1]);
        const startDay = parseInt(probMatch[2]);
        const startHour = parseInt(probMatch[3]);
        const endDay = parseInt(probMatch[4]);
        const endHour = parseInt(probMatch[5]);
        const inWindow = isInMissionWindow(startDay, startHour, endDay, endHour);
        if (includeAll || inWindow) {
          sections.push({ type: `PROB${prob}`, content: probMatch[6], start: startHour, end: endHour, startDay, endDay, inMissionWindow: inWindow });
        }
      }
      
      // Extract BECMG periods
      const becmgRegex = /BECMG\s+(\d{2})(\d{2})\/(\d{2})(\d{2})\s+([^]*?)(?=FM|TEMPO|BECMG|PROB|$)/gi;
      let becmgMatch;
      while ((becmgMatch = becmgRegex.exec(tafRaw)) !== null) {
        const startDay = parseInt(becmgMatch[1]);
        const startHour = parseInt(becmgMatch[2]);
        const endDay = parseInt(becmgMatch[3]);
        const endHour = parseInt(becmgMatch[4]);
        const inWindow = isInMissionWindow(startDay, startHour, endDay, endHour);
        if (includeAll || inWindow) {
          sections.push({ type: 'BECMG', content: becmgMatch[5], start: startHour, end: endHour, startDay, endDay, inMissionWindow: inWindow });
        }
      }
      
      return sections;
    };
    
    // ========================================
    // FLIGHT LIMITATIONS CHECKLIST (User-specified GO/NO GO criteria for EBOS)
    // 1. Crosswind > 20kts (wind or gusting)
    // 2. Icing possibility from 0-2500ft with clouds in that range
    // 3. Poor visibility after sunset
    // 4. High possibility of Thunderstorms along routing
    // ========================================
    let flightLimitations = {
      poorVisibility: false,
      poorVisibilityAfterSunset: false, // New: specific check for visibility after sunset
      lowClouds: false,
      crosswindExceeds20: false,
      thunderstormCB: false,
      knownIcingConditions: false,
      icingWithClouds0to2500: false // New: icing + clouds in 0-2500ft range
    };
    let limitationReasons = {
      poorVisibility: [],
      poorVisibilityAfterSunset: [],
      lowClouds: [],
      crosswindExceeds20: [],
      thunderstormCB: [],
      knownIcingConditions: [],
      icingWithClouds0to2500: []
    };
    
    // Analyze CURRENT conditions (METAR)
    const categories = filteredWeatherData.map(a => a.metar?.flight_category).filter(Boolean);
    // Current METAR flight categories (only used for display, not GO/NO GO unless within 30min of mission)
    const currentHasIFR = categories.includes('IFR') || categories.includes('LIFR');
    const currentHasLIFR = categories.includes('LIFR');
    const currentHasMVFR = categories.includes('MVFR');
    
    // ========================================
    // CHECK 1: POOR VISIBILITY (< 4000m) - FROM METAR
    // Only used for GO/NO GO if briefing is < 30 min before mission start
    // ========================================
    if (useMetarForDecision) {
      filteredWeatherData.forEach(a => {
        if (a.metar?.raw) {
          const visMatch = a.metar.raw.match(/\s(\d{4})\s/);
          if (visMatch) {
            const vis = parseInt(visMatch[1]);
            if (vis <= REDUCED_VISIBILITY) {
              flightLimitations.poorVisibility = true;
              limitationReasons.poorVisibility.push(`${a.icao}: METAR visibility ${vis}m`);
              
              // Check if this is after sunset - becomes NO GO
              if (isMissionAfterSunset) {
                flightLimitations.poorVisibilityAfterSunset = true;
                limitationReasons.poorVisibilityAfterSunset.push(`${a.icao}: METAR visibility ${vis}m after sunset (est. ${estimatedSunsetHour}:00Z)`);
              }
            }
          }
          // Check for TEMPO reduced visibility in METAR
          if (a.metar.raw.includes('TEMPO') && a.metar.raw.match(/TEMPO[^]*(\d{4})/)) {
            const tempoVis = parseInt(a.metar.raw.match(/TEMPO[^]*(\d{4})/)[1]);
            if (tempoVis <= REDUCED_VISIBILITY) {
              flightLimitations.poorVisibility = true;
              limitationReasons.poorVisibility.push(`${a.icao}: METAR TEMPO visibility ${tempoVis}m`);
              
              if (isMissionAfterSunset) {
                flightLimitations.poorVisibilityAfterSunset = true;
                limitationReasons.poorVisibilityAfterSunset.push(`${a.icao}: METAR TEMPO visibility ${tempoVis}m after sunset`);
              }
            }
          }
        }
      });
    }
    
    // ========================================
    // CHECK 2: LOW CLOUDS (base < 2500ft for icing check) - FROM METAR
    // Only used for GO/NO GO if briefing is < 30 min before mission start
    // ========================================
    let cloudsIn0to2500Range = false;
    let cloudsIn0to2500Details = [];
    
    if (useMetarForDecision) {
      filteredWeatherData.forEach(a => {
        if (a.metar?.raw) {
          const cloudMatches = [...a.metar.raw.matchAll(/(FEW|SCT|BKN|OVC)(\d{3})/g)];
          cloudMatches.forEach(match => {
            const coverage = match[1];
            const altitude = parseInt(match[2]) * 100;
            
            // Track clouds in 0-2500ft range for icing combination check
            if (altitude >= ICING_ALTITUDE_LOW && altitude <= ICING_ALTITUDE_HIGH && 
                (coverage === 'BKN' || coverage === 'OVC' || coverage === 'SCT')) {
              cloudsIn0to2500Range = true;
              cloudsIn0to2500Details.push(`${a.icao}: ${coverage}@${altitude}ft (METAR)`);
            }
            
            // General low cloud check (below 1500ft)
            if (altitude < 1500 && (coverage === 'BKN' || coverage === 'OVC' || coverage === 'SCT')) {
              flightLimitations.lowClouds = true;
              limitationReasons.lowClouds.push(`${a.icao}: ${coverage}@${altitude}ft (METAR)`);
            }
          });
        }
      });
    }
    
    // ========================================
    // CHECK 3: CROSSWIND > 20 KTS (with gusts) at EBOS - FROM METAR
    // User criteria: "Crosswind more than 20kts during timeframe, wind or gusting wind"
    // Only used for GO/NO GO if briefing is < 30 min before mission start
    // ========================================
    const winds = filteredWeatherData.map(a => {
      if (a.metar?.raw) {
        const windMatch = a.metar.raw.match(/(\d{3})(\d{2})(G(\d{2}))?KT/);
        if (windMatch) {
          const direction = parseInt(windMatch[1]);
          const speed = parseInt(windMatch[2]);
          const gusts = windMatch[4] ? parseInt(windMatch[4]) : null;
          
          // Calculate crosswind component for EBOS (use gusts if available - per user spec)
          const windAngle = Math.abs(direction - RUNWAY_HEADING);
          const crosswindComponent = Math.round((gusts || speed) * Math.sin(windAngle * Math.PI / 180));
          
          return {
            airport: a.icao,
            direction,
            speed,
            gusts,
            crosswind: Math.abs(crosswindComponent)
          };
        }
      }
      return null;
    }).filter(Boolean);
    
    const ebosWind = winds.find(w => w.airport === DEPARTURE_AIRPORT);
    const ebosCrosswind = ebosWind?.crosswind || 0;
    
    // Only apply METAR crosswind check if within 30 min of mission start
    if (useMetarForDecision && ebosCrosswind > CROSSWIND_LIMIT) {
      flightLimitations.crosswindExceeds20 = true;
      const gustInfo = ebosWind?.gusts ? ` (gusting ${ebosWind.gusts}KT)` : '';
      limitationReasons.crosswindExceeds20.push(`EBOS: METAR crosswind ${ebosCrosswind}KT${gustInfo} exceeds ${CROSSWIND_LIMIT}KT limit`);
    }
    
    // ========================================
    // CHECK 4: THUNDERSTORM / CB - FROM METAR (only if within 30 min) AND TAF
    // ========================================
    if (useMetarForDecision) {
      filteredWeatherData.forEach(a => {
        if (a.metar?.raw) {
          if (a.metar.raw.includes(' CB') || a.metar.raw.includes(' TS') || a.metar.raw.includes('+TS')) {
            flightLimitations.thunderstormCB = true;
            limitationReasons.thunderstormCB.push(`${a.icao}: METAR - Thunderstorm/CB reported`);
          }
        }
      });
    }
    // Always check TAF for thunderstorms
    weatherData.forEach(a => {
      if (a.taf?.raw) {
        if (a.taf.raw.includes(' CB') || a.taf.raw.includes(' TS') || a.taf.raw.includes('+TS')) {
          flightLimitations.thunderstormCB = true;
          limitationReasons.thunderstormCB.push(`${a.icao}: Thunderstorm/CB forecast`);
        }
      }
    });
    
    // ========================================
    // CHECK 5: KNOWN ICING CONDITIONS
    // - Snow/rain precipitation
    // - Freezing level near surface (temp near 0°C)
    // - FZRA, FZFG, etc.
    // METAR only used if within 30 min of mission start
    // ========================================
    if (useMetarForDecision) {
      filteredWeatherData.forEach(a => {
        if (a.metar?.raw) {
          const metar = a.metar.raw;
          
          // Check for freezing precipitation
          if (metar.includes('FZRA') || metar.includes('FZFG') || metar.includes('FZDZ')) {
            flightLimitations.knownIcingConditions = true;
            limitationReasons.knownIcingConditions.push(`${a.icao}: METAR - Freezing precipitation`);
          }
          
          // Check for snow/rain mix (potential icing)
          if ((metar.includes(' SN') || metar.includes(' SHSN') || metar.includes('-SN')) && 
              (metar.includes(' RA') || metar.includes(' SHRA') || metar.includes('-RA') || metar.includes('RASN') || metar.includes('SNRA'))) {
            flightLimitations.knownIcingConditions = true;
            limitationReasons.knownIcingConditions.push(`${a.icao}: METAR - Snow/rain mix - icing risk`);
          }
          
          // Check temperature near freezing with precipitation
          const tempMatch = metar.match(/\s(M?\d{2})\/(M?\d{2})\s/);
          if (tempMatch) {
            const temp = tempMatch[1].startsWith('M') ? -parseInt(tempMatch[1].slice(1)) : parseInt(tempMatch[1]);
            if (temp <= 3 && temp >= -5) {
              // Near freezing - check for any precipitation
              if (metar.includes(' SN') || metar.includes(' RA') || metar.includes('SHSN') || metar.includes('SHRA')) {
                flightLimitations.knownIcingConditions = true;
                limitationReasons.knownIcingConditions.push(`${a.icao}: METAR - Temp ${temp}°C with precipitation - icing risk`);
              }
            }
          }
        }
      });
    }
    
    // Always check TAF for icing conditions (only in mission time window)
    const relevantTafSections = extractRelevantTafSections(departureData?.taf?.raw);
    
    if (relevantTafSections.length > 0) {
      relevantTafSections.forEach(section => {
        if (!section.inMissionWindow) return; // Only check sections in mission window
        
        const content = section.content;
        if (content.includes('FZRA') || content.includes('FZFG') || content.includes('FZDZ')) {
          flightLimitations.knownIcingConditions = true;
          limitationReasons.knownIcingConditions.push(`TAF ${section.type}: Freezing precipitation (${section.start}-${section.end || ''}Z)`);
        }
        if ((content.includes(' SN') || content.includes('SHSN')) && (content.includes(' RA') || content.includes('SHRA'))) {
          flightLimitations.knownIcingConditions = true;
          limitationReasons.knownIcingConditions.push(`TAF ${section.type}: Snow/rain (${section.start}-${section.end || ''}Z)`);
        }
      });
    }
    
    // ========================================
    // CHECK 6: ICING + CLOUDS IN 0-2500ft COMBINATION
    // User criteria: "Icing possibility from 0 to 2500ft in combination with clouds between these altitudes"
    // ========================================
    if (flightLimitations.knownIcingConditions && cloudsIn0to2500Range) {
      flightLimitations.icingWithClouds0to2500 = true;
      limitationReasons.icingWithClouds0to2500.push(`Icing conditions + clouds in ${ICING_ALTITUDE_LOW}-${ICING_ALTITUDE_HIGH}ft range:`);
      limitationReasons.knownIcingConditions.forEach(r => limitationReasons.icingWithClouds0to2500.push(`  ${r}`));
      cloudsIn0to2500Details.forEach(r => limitationReasons.icingWithClouds0to2500.push(`  ${r}`));
    }
    
    // ========================================
    // TAF ANALYSIS - CHECK ALL ROUTE AIRPORTS FOR MISSION TIME WINDOW
    // TEMPO messages are only considered if:
    // 1. They fall within the mission window
    // 2. They affect >50% of waypoints OR multiple airports (not just one isolated location)
    // ========================================
    let tafForecastsIFR = false;
    let tafForecastsLIFR = false;
    let tafIssues = [];
    let tafTimeWindowInfo = [];
    
    // Track TEMPO issues by type to check if they affect multiple airports
    const tempoIssuesByType = {
      lowVis: [], // airports with low visibility TEMPO
      lowCeiling: [], // airports with low ceiling TEMPO
      fog: [], // airports with fog TEMPO
      thunderstorm: [], // airports with TS/CB TEMPO
    };
    
    // Non-TEMPO issues (FM, BECMG, BASE) - these are more certain and treated differently
    const permanentIssues = {
      lowVis: [],
      lowCeiling: [],
      fog: [],
      thunderstorm: [],
    };
    
    // Loop through ALL airports on the route to check TAF for IFR conditions
    filteredWeatherData.forEach(airportData => {
      const airportIcao = airportData.icao;
      const airportTafSections = extractRelevantTafSections(airportData?.taf?.raw);
      
      if (airportTafSections.length > 0) {
        airportTafSections.forEach(section => {
          if (!section.inMissionWindow) return; // Only check sections in mission window
          
          const content = section.content;
          const timeLabel = section.end ? `${section.start}-${section.end}Z` : `FM${section.start}Z`;
          const isTempo = section.type === 'TEMPO' || section.type.startsWith('PROB');
          const issueTracker = isTempo ? tempoIssuesByType : permanentIssues;
          
          // Check for visibility less than 5000m (IFR) or less than 1500m (LIFR)
          const visMatches = [...content.matchAll(/(\d{4})\s/g)];
          visMatches.forEach(match => {
            const vis = parseInt(match[1]);
            if (vis < 1500) {
              issueTracker.lowVis.push({ icao: airportIcao, type: section.type, value: vis, severity: 'LIFR' });
              tafIssues.push(`${airportIcao} ${section.type} ${timeLabel}: Vis ${vis}m (LIFR)`);
            } else if (vis < 5000) {
              issueTracker.lowVis.push({ icao: airportIcao, type: section.type, value: vis, severity: 'IFR' });
              tafIssues.push(`${airportIcao} ${section.type} ${timeLabel}: Vis ${vis}m (IFR)`);
            }
            if (vis <= REDUCED_VISIBILITY && !isTempo) {
              // Only permanent issues trigger flight limitations directly
              flightLimitations.poorVisibility = true;
              limitationReasons.poorVisibility.push(`${airportIcao} TAF ${section.type} ${timeLabel}: Vis ${vis}m`);
            }
          });
          
          // Check for low ceilings
          const cloudMatches = [...content.matchAll(/(BKN|OVC|SCT)(\d{3})/g)];
          cloudMatches.forEach(match => {
            const ceiling = parseInt(match[2]) * 100;
            if (ceiling < 500) {
              issueTracker.lowCeiling.push({ icao: airportIcao, type: section.type, value: ceiling, severity: 'LIFR' });
              tafIssues.push(`${airportIcao} ${section.type} ${timeLabel}: ${match[1]}@${ceiling}ft (LIFR)`);
            } else if (ceiling < 1000) {
              issueTracker.lowCeiling.push({ icao: airportIcao, type: section.type, value: ceiling, severity: 'IFR' });
              tafIssues.push(`${airportIcao} ${section.type} ${timeLabel}: ${match[1]}@${ceiling}ft (IFR)`);
            }
            if (ceiling < LOW_CLOUD_LIMIT && !isTempo) {
              flightLimitations.lowClouds = true;
              limitationReasons.lowClouds.push(`${airportIcao} TAF ${section.type} ${timeLabel}: ${match[1]}@${ceiling}ft`);
            }
          });
          
          // Check for fog
          if (content.includes(' FG ') || content.includes(' FZFG ')) {
            issueTracker.fog.push({ icao: airportIcao, type: section.type });
            tafIssues.push(`${airportIcao} ${section.type} ${timeLabel}: Fog`);
            if (!isTempo) {
              flightLimitations.poorVisibility = true;
              limitationReasons.poorVisibility.push(`${airportIcao} TAF ${section.type} ${timeLabel}: Fog`);
            }
          }
          
          // Check for thunderstorms
          if (content.includes(' TS') || content.includes(' CB')) {
            issueTracker.thunderstorm.push({ icao: airportIcao, type: section.type });
            if (!isTempo) {
              flightLimitations.thunderstormCB = true;
              limitationReasons.thunderstormCB.push(`${airportIcao} TAF ${section.type} ${timeLabel}: TS/CB`);
            }
          }
          
          // Store time window info for display
          tafTimeWindowInfo.push({ icao: airportIcao, type: section.type, start: section.start, end: section.end });
        });
      }
    });
    
    // ========================================
    // TEMPO IMPACT ASSESSMENT
    // TEMPO only triggers NO GO if:
    // - Same issue affects MULTIPLE airports (2+), OR
    // - >50% of route waypoints are affected
    // Otherwise it's "GO WITH CAUTION" - pilots can assess "on the go"
    // ========================================
    const totalAirports = filteredWeatherData.length;
    const totalWaypoints = currentRouteWaypointWeather?.waypoints?.length || 0;
    
    // Check TEMPO impacts
    const getUniqueAirports = (issues) => [...new Set(issues.map(i => i.icao))];
    
    const tempoLowVisAirports = getUniqueAirports(tempoIssuesByType.lowVis);
    const tempoLowCeilingAirports = getUniqueAirports(tempoIssuesByType.lowCeiling);
    const tempoFogAirports = getUniqueAirports(tempoIssuesByType.fog);
    const tempoTsAirports = getUniqueAirports(tempoIssuesByType.thunderstorm);
    
    // TEMPO affects majority if 2+ airports have same issue OR >50% of route
    const tempoLowVisMajority = tempoLowVisAirports.length >= 2 || (totalAirports > 0 && tempoLowVisAirports.length / totalAirports > 0.5);
    const tempoLowCeilingMajority = tempoLowCeilingAirports.length >= 2 || (totalAirports > 0 && tempoLowCeilingAirports.length / totalAirports > 0.5);
    const tempoFogMajority = tempoFogAirports.length >= 2 || (totalAirports > 0 && tempoFogAirports.length / totalAirports > 0.5);
    const tempoTsMajority = tempoTsAirports.length >= 2 || (totalAirports > 0 && tempoTsAirports.length / totalAirports > 0.5);
    
    // Track localized TEMPO issues (single airport - don't trigger NO GO)
    const localizedTempoIssues = [];
    if (tempoLowVisAirports.length === 1 && !tempoLowVisMajority) {
      localizedTempoIssues.push(`Low vis TEMPO at ${tempoLowVisAirports[0]} - assess on the go`);
    }
    if (tempoLowCeilingAirports.length === 1 && !tempoLowCeilingMajority) {
      localizedTempoIssues.push(`Low ceiling TEMPO at ${tempoLowCeilingAirports[0]} - assess on the go`);
    }
    if (tempoFogAirports.length === 1 && !tempoFogMajority) {
      localizedTempoIssues.push(`Fog TEMPO at ${tempoFogAirports[0]} - assess on the go`);
    }
    if (tempoTsAirports.length === 1 && !tempoTsMajority) {
      localizedTempoIssues.push(`TS/CB TEMPO at ${tempoTsAirports[0]} - assess on the go`);
    }
    
    // Determine TAF IFR/LIFR status based on:
    // 1. Permanent issues (FM, BECMG, BASE) - always count
    // 2. TEMPO issues - only if affecting majority of route
    const hasPermLIFR = permanentIssues.lowVis.some(i => i.severity === 'LIFR') || permanentIssues.lowCeiling.some(i => i.severity === 'LIFR');
    const hasPermIFR = permanentIssues.lowVis.some(i => i.severity === 'IFR') || permanentIssues.lowCeiling.some(i => i.severity === 'IFR') || permanentIssues.fog.length > 0;
    const hasTempoMajorityLIFR = (tempoLowVisMajority && tempoIssuesByType.lowVis.some(i => i.severity === 'LIFR')) || 
                                  (tempoLowCeilingMajority && tempoIssuesByType.lowCeiling.some(i => i.severity === 'LIFR'));
    const hasTempoMajorityIFR = (tempoLowVisMajority && tempoIssuesByType.lowVis.length > 0) || 
                                 (tempoLowCeilingMajority && tempoIssuesByType.lowCeiling.length > 0) || 
                                 tempoFogMajority;
    
    tafForecastsLIFR = hasPermLIFR || hasTempoMajorityLIFR;
    tafForecastsIFR = hasPermIFR || hasTempoMajorityIFR;
    
    // Thunderstorms - only NO GO if permanent OR affecting majority
    if (permanentIssues.thunderstorm.length > 0 || tempoTsMajority) {
      flightLimitations.thunderstormCB = true;
      if (tempoTsMajority && permanentIssues.thunderstorm.length === 0) {
        limitationReasons.thunderstormCB.push(`TEMPO TS/CB affecting multiple airports: ${tempoTsAirports.join(', ')}`);
      }
    }
    
    // TEMPO low vis/ceiling affecting majority triggers flight limitations
    if (tempoLowVisMajority) {
      flightLimitations.poorVisibility = true;
      limitationReasons.poorVisibility.push(`TEMPO low visibility affecting multiple airports: ${tempoLowVisAirports.join(', ')}`);
    }
    if (tempoLowCeilingMajority) {
      flightLimitations.lowClouds = true;
      limitationReasons.lowClouds.push(`TEMPO low ceiling affecting multiple airports: ${tempoLowCeilingAirports.join(', ')}`);
    }
    
    // Combined IFR check - only use current METAR IFR if within 30 min of mission
    const hasIFR = (useMetarForDecision && currentHasIFR) || tafForecastsIFR;
    const hasLIFR = (useMetarForDecision && currentHasLIFR) || tafForecastsLIFR;
    const hasMVFR = useMetarForDecision && currentHasMVFR;
    
    // Check enroute cloud ceilings against mission altitude
    const enrouteWaypoints = currentRouteWaypointWeather?.waypoints || [];
    const enrouteWithCeiling = enrouteWaypoints.filter(w => w.weather_summary?.cloud_ceiling_ft != null);
    const enrouteBelowMin = enrouteWithCeiling.filter(w => w.weather_summary.cloud_ceiling_ft < 2000);
    const enrouteBelowTarget = enrouteWithCeiling.filter(w => w.weather_summary.cloud_ceiling_ft < 2500);
    
    if (enrouteBelowMin.length > 0 && enrouteWithCeiling.length > 0) {
      flightLimitations.lowClouds = true;
      const lowestEnroute = Math.min(...enrouteBelowMin.map(w => w.weather_summary.cloud_ceiling_ft));
      limitationReasons.lowClouds.push(`Enroute ceiling ~${lowestEnroute}ft at ${enrouteBelowMin.length}/${enrouteWaypoints.length} waypoints — below 2000ft mission minimum`);
    } else if (enrouteBelowTarget.length > 0 && enrouteWithCeiling.length > 0) {
      flightLimitations.lowClouds = true;
      const lowestEnroute = Math.min(...enrouteBelowTarget.map(w => w.weather_summary.cloud_ceiling_ft));
      limitationReasons.lowClouds.push(`Enroute ceiling ~${lowestEnroute}ft at ${enrouteBelowTarget.length}/${enrouteWaypoints.length} waypoints — below 2500ft mission target`);
    }
    
    // ========================================
    // DETERMINE MISSION SAFETY OUTCOME
    // Based on user-specified GO/NO GO criteria for EBOS:
    // 1. Crosswind > 20kts (wind or gusting) during timeframe
    // 2. Icing possibility from 0-2500ft + clouds in that range
    // 3. Poor visibility after sunset
    // 4. High possibility of Thunderstorms along routing
    // Note: METAR data only used if briefing < 30 min before mission start
    // ========================================
    let missionSafetyOutcome = "GO";
    let missionSafetyReason = [];
    
    // Priority 1: IFR/LIFR conditions (from TAF, or METAR if within 30 min)
    if (hasLIFR || hasIFR) {
      missionSafetyOutcome = "NO GO";
      if (hasLIFR) {
        missionSafetyReason.push("LIFR conditions - VFR patrol not possible");
      } else {
        missionSafetyReason.push("IFR conditions forecast - VFR patrol not possible");
      }
      tafIssues.forEach(i => missionSafetyReason.push(i));
      missionSafetyReason.push("Note: IFR capability is for abort/return only");
    }
    // Priority 2: User-specified NO GO criteria
    // Criterion 4: Thunderstorms along routing
    else if (flightLimitations.thunderstormCB) {
      missionSafetyOutcome = "NO GO";
      missionSafetyReason.push("☒ Thunderstorms along routing");
      limitationReasons.thunderstormCB.forEach(r => missionSafetyReason.push(r));
    }
    // Criterion 1: Crosswind > 20 KTS at EBOS
    else if (flightLimitations.crosswindExceeds20) {
      missionSafetyOutcome = "NO GO";
      missionSafetyReason.push("☒ Crosswind > 20 KTS at EBOS");
      limitationReasons.crosswindExceeds20.forEach(r => missionSafetyReason.push(r));
    }
    // Criterion 2: Icing + clouds in 0-2500ft range
    else if (flightLimitations.icingWithClouds0to2500) {
      missionSafetyOutcome = "NO GO";
      missionSafetyReason.push("☒ Icing conditions with clouds 0-2500ft");
      limitationReasons.icingWithClouds0to2500.forEach(r => missionSafetyReason.push(r));
    }
    // Criterion 3: Poor visibility after sunset
    else if (flightLimitations.poorVisibilityAfterSunset) {
      missionSafetyOutcome = "NO GO";
      missionSafetyReason.push("☒ Poor visibility after sunset");
      limitationReasons.poorVisibilityAfterSunset.forEach(r => missionSafetyReason.push(r));
    }
    // Additional safety checks (not in user's specific criteria but good practice)
    else if (flightLimitations.knownIcingConditions && !cloudsIn0to2500Range) {
      // Icing without the cloud combination - caution
      missionSafetyOutcome = "GO WITH CAUTION";
      missionSafetyReason.push("⚠ Icing conditions reported (clouds not in 0-2500ft)");
      limitationReasons.knownIcingConditions.forEach(r => missionSafetyReason.push(r));
    }
    else if (flightLimitations.poorVisibility && flightLimitations.lowClouds) {
      missionSafetyOutcome = "GO WITH CAUTION";
      missionSafetyReason.push("⚠ Poor visibility + Low clouds");
      limitationReasons.poorVisibility.forEach(r => missionSafetyReason.push(r));
      limitationReasons.lowClouds.forEach(r => missionSafetyReason.push(r));
    }
    else if (flightLimitations.poorVisibility && !isMissionAfterSunset) {
      missionSafetyOutcome = "GO WITH CAUTION";
      missionSafetyReason.push("⚠ Poor visibility (before sunset)");
      limitationReasons.poorVisibility.forEach(r => missionSafetyReason.push(r));
    }
    else if (flightLimitations.lowClouds) {
      missionSafetyOutcome = "GO WITH CAUTION";
      missionSafetyReason.push("⚠ Low clouds");
      limitationReasons.lowClouds.forEach(r => missionSafetyReason.push(r));
    }
    else if (hasMVFR) {
      missionSafetyOutcome = "GO WITH CAUTION";
      missionSafetyReason.push("MVFR conditions - marginal VFR");
    }
    // Localized TEMPO issues - single airport affected, can assess "on the go"
    else if (localizedTempoIssues.length > 0) {
      missionSafetyOutcome = "GO WITH CAUTION";
      missionSafetyReason.push("⚠ Localized weather - assess on the go");
      localizedTempoIssues.forEach(issue => missionSafetyReason.push(`  • ${issue}`));
    }
    
    if (missionSafetyOutcome === "GO") {
      missionSafetyReason.push("✓ All GO/NO GO criteria checked - conditions acceptable");
      missionSafetyReason.push(`✓ Crosswind ${ebosCrosswind}KT within ${CROSSWIND_LIMIT}KT limit`);
      missionSafetyReason.push(`✓ No icing + cloud combination in 0-2500ft`);
      missionSafetyReason.push(`✓ No thunderstorms along routing`);
      if (isMissionAfterSunset) {
        missionSafetyReason.push(`✓ Visibility acceptable for post-sunset ops`);
      }
      // Add note about localized TEMPO if any exist but not triggering caution
      if (localizedTempoIssues.length > 0) {
        missionSafetyReason.push(`ℹ Localized TEMPO at single locations - assess on the go`);
      }
    }
    
    // ========================================
    // DETERMINE PATROL EFFECTIVENESS OUTCOME
    // ========================================
    // Based on Wescam MX-15 camera system capabilities:
    // - EO (daylight/spotter): Cannot see through clouds. Needs clear line-of-sight to surface.
    // - IR (thermal): Can penetrate thin cloud layers but degrades in thick overcast.
    // - SWIR/Low-light: Better haze penetration than EO, still blocked by clouds.
    // - All sensors: Rain on optics degrades image quality. Fog reduces EO range significantly.
    // Mission altitude ~2500ft (min 2000ft). If cloud ceiling < mission alt, sensors look into cloud base.
    
    let patrolOutcome = "EFFECTIVE";
    let patrolReason = [];
    
    // Check enroute cloud ceilings from waypoint data
    const routeWaypoints = currentRouteWaypointWeather?.waypoints || [];
    const waypointsWithCeiling = routeWaypoints.filter(w => w.weather_summary?.cloud_ceiling_ft != null);
    const lowestCeiling = waypointsWithCeiling.length > 0 
      ? Math.min(...waypointsWithCeiling.map(w => w.weather_summary.cloud_ceiling_ft)) 
      : null;
    const waypointsBelowMin = waypointsWithCeiling.filter(w => w.weather_summary.cloud_ceiling_ft < 2000);
    const waypointsBelowTarget = waypointsWithCeiling.filter(w => w.weather_summary.cloud_ceiling_ft < 2500);
    const hasEnrouteRain = routeWaypoints.some(w => {
      const wid = w.weather_summary?.weather_id || 800;
      return wid >= 200 && wid < 600;
    });
    const hasEnrouteFog = routeWaypoints.some(w => {
      const wid = w.weather_summary?.weather_id || 800;
      return wid >= 700 && wid < 800;
    });
    
    if (hasLIFR || hasIFR || missionSafetyOutcome === "NO GO") {
      patrolOutcome = "NOT POSSIBLE";
      if (flightLimitations.lowClouds) {
        patrolReason.push("Cloud base below mission altitude — EO/SWIR cameras cannot image through clouds");
        patrolReason.push("IR thermal may detect large heat signatures through thin layers but no usable imagery");
      }
      if (flightLimitations.poorVisibility) {
        patrolReason.push("Fog/mist severely degrades EO sensor range — ground targets not visible");
        patrolReason.push("IR thermal partially penetrates fog but with significant range reduction");
      }
      if (!flightLimitations.lowClouds && !flightLimitations.poorVisibility) {
        patrolReason.push("IFR conditions prevent safe visual surveillance operations");
      }
      patrolReason.push("MX-15 sensors require line-of-sight to surface — VFR patrol not feasible");
    } else if (waypointsBelowMin.length > 0 && waypointsWithCeiling.length > 0) {
      // Enroute ceilings below 2000ft minimum
      const pctAffected = Math.round((waypointsBelowMin.length / Math.max(routeWaypoints.length, 1)) * 100);
      if (pctAffected >= 50) {
        patrolOutcome = "NOT POSSIBLE";
        patrolReason.push(`Cloud ceiling below 2000ft at ${waypointsBelowMin.length}/${routeWaypoints.length} waypoints (${pctAffected}% of route)`);
        patrolReason.push("EO/SWIR cameras blind — looking into cloud base from mission altitude");
        patrolReason.push("IR thermal severely degraded through overcast layer");
      } else {
        patrolOutcome = "REDUCED";
        patrolReason.push(`Cloud ceiling below 2000ft at ${waypointsBelowMin.length}/${routeWaypoints.length} enroute points`);
        patrolReason.push("EO camera blind in affected sectors — partial coverage gaps");
        patrolReason.push("IR thermal may provide limited detection through thin cloud patches");
      }
    } else if (waypointsBelowTarget.length > 0 && waypointsWithCeiling.length > 0) {
      // Enroute ceilings between 2000-2500ft — reduced but workable
      patrolOutcome = "REDUCED";
      patrolReason.push(`Cloud ceiling 2000-2500ft at ${waypointsBelowTarget.length}/${routeWaypoints.length} enroute points`);
      patrolReason.push("EO camera range reduced — limited altitude margin below cloud base");
      patrolReason.push("IR thermal effective but EO zoom/spotter performance degraded at low altitude");
    } else if (flightLimitations.lowClouds) {
      patrolOutcome = "REDUCED";
      patrolReason.push("Low cloud coverage reported at airport(s) — may extend to patrol area");
      patrolReason.push("EO camera ground visibility compromised if clouds persist enroute");
      limitationReasons.lowClouds.forEach(r => patrolReason.push(r));
    } else if (flightLimitations.poorVisibility) {
      patrolOutcome = "REDUCED";
      patrolReason.push("Reduced visibility degrades EO sensor effective range");
      patrolReason.push("MX-15 haze penetration (ELAP) helps but cannot fully compensate");
      limitationReasons.poorVisibility.forEach(r => patrolReason.push(r));
    }
    
    // Additional sensor-specific notes
    if (hasEnrouteRain && patrolOutcome !== "NOT POSSIBLE") {
      if (patrolOutcome === "EFFECTIVE") patrolOutcome = "REDUCED";
      patrolReason.push("Rain along route — water on optics degrades all sensor imagery");
    }
    if (hasEnrouteFog && patrolOutcome !== "NOT POSSIBLE") {
      if (patrolOutcome === "EFFECTIVE") patrolOutcome = "REDUCED";
      patrolReason.push("Fog/mist enroute — EO range severely limited, IR partially affected");
    }
    
    if (patrolOutcome === "EFFECTIVE") {
      patrolReason.push("Clear conditions — all MX-15 sensors (EO/IR/SWIR) fully operational");
      patrolReason.push("Good line-of-sight to surface expected along route");
      if (lowestCeiling != null) {
        patrolReason.push(`Lowest enroute ceiling ~${lowestCeiling}ft — adequate clearance above mission altitude`);
      }
    }
    
    // Determine overall risk for UI color
    let risk = "LOW";
    if (missionSafetyOutcome === "NO GO") {
      risk = "HIGH";
    } else if (missionSafetyOutcome === "GO WITH CAUTION" || patrolOutcome === "REDUCED") {
      risk = "MODERATE";
    }
    
    // ========================================
    // BUILD ANALYSIS TEXT
    // ========================================
    let analysisLines = [];
    
    // Header
    analysisLines.push(`WEATHER ANALYSIS - FL JO FRANCE`);
    analysisLines.push(`${DEPARTURE_AIRPORT} ${DEPARTURE_TIME} → ${ARRIVAL_TIME} | VFR Required`);
    analysisLines.push(``);
    
    // Current conditions (METAR)
    analysisLines.push(`CURRENT CONDITIONS (METAR):`);
    weatherData.forEach(a => {
      const cat = a.metar?.flight_category || 'N/A';
      const wind = winds.find(w => w.airport === a.icao);
      const windStr = wind ? `${wind.direction}°/${wind.speed}${wind.gusts ? `G${wind.gusts}` : ''}KT` : '-';
      analysisLines.push(`  ${a.icao}: ${cat} | ${windStr}`);
    });
    analysisLines.push(``);
    
    // TAF summary
    if (departureData?.taf?.raw) {
      analysisLines.push(`FORECAST (TAF ${DEPARTURE_AIRPORT}):`);
      const tafLines = departureData.taf.raw.split('\n').slice(0, 3);
      tafLines.forEach(line => {
        if (line.trim()) analysisLines.push(`  ${line.trim().substring(0, 60)}${line.length > 60 ? '...' : ''}`);
      });
      if (tafForecastsIFR || tafForecastsLIFR) {
        analysisLines.push(`  ⚠ IFR CONDITIONS EXPECTED`);
      }
      analysisLines.push(``);
    }
    
    // NSAS Limits & Conditions
    analysisLines.push(`NSAS LIMITS & CONDITIONS:`);
    if (flightLimitations.poorVisibility) {
      analysisLines.push(`  ⚠ Reduced visibility: ≤${REDUCED_VISIBILITY}m`);
    }
    if (flightLimitations.lowClouds) {
      analysisLines.push(`  ⚠ Low clouds: Base <${LOW_CLOUD_LIMIT}ft`);
    }
    if (flightLimitations.knownIcingConditions) {
      analysisLines.push(`  ⚠ Icing conditions present`);
    }
    if (!flightLimitations.poorVisibility && !flightLimitations.lowClouds && !flightLimitations.knownIcingConditions) {
      analysisLines.push(`  ✓ Within limits`);
    }
    analysisLines.push(``);
    
    // Flight Limitations Checklist
    analysisLines.push(`FLIGHT LIMITATIONS CHECKLIST:`);
    analysisLines.push(`  ${flightLimitations.poorVisibility ? '☑' : '☐'} Poor visibility`);
    analysisLines.push(`  ${flightLimitations.lowClouds ? '☑' : '☐'} Low clouds`);
    analysisLines.push(`  ${flightLimitations.crosswindExceeds20 ? '☑' : '☐'} Crosswind > 20 KTS`);
    analysisLines.push(`  ${flightLimitations.thunderstormCB ? '☑' : '☐'} Thunderstorm / CB`);
    analysisLines.push(`  ${flightLimitations.knownIcingConditions ? '☑' : '☐'} Known icing conditions`);
    analysisLines.push(``);
    
    // DECISION 1: MISSION SAFETY
    analysisLines.push(`━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`);
    analysisLines.push(`1. MISSION SAFETY`);
    analysisLines.push(``);
    
    const safetyIcon = missionSafetyOutcome === "GO" ? "✓" : 
                       missionSafetyOutcome === "GO WITH CAUTION" ? "⚠" : "✗";
    analysisLines.push(`   ${safetyIcon} ${missionSafetyOutcome}`);
    analysisLines.push(``);
    missionSafetyReason.forEach(r => analysisLines.push(`   • ${r}`));
    
    if (ebosWind && missionSafetyOutcome !== "NO GO") {
      analysisLines.push(``);
      analysisLines.push(`   Crosswind: ${ebosCrosswind}KT (limit: ${CROSSWIND_LIMIT}KT)`);
    }
    
    analysisLines.push(``);
    analysisLines.push(`   Abort option: IFR return via ILS RWY 07/25`);
    analysisLines.push(`   Alternates: LFAC (Calais), LFQQ (Lille)`);
    analysisLines.push(``);
    
    // DECISION 2: PATROL EFFECTIVENESS
    analysisLines.push(`━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`);
    analysisLines.push(`2. PATROL EFFECTIVENESS (MX-15 Sensor Assessment)`);
    analysisLines.push(``);
    
    const patrolIcon = patrolOutcome === "EFFECTIVE" ? "✓" : 
                       patrolOutcome === "REDUCED" ? "⚠" : "✗";
    analysisLines.push(`   ${patrolIcon} ${patrolOutcome}`);
    analysisLines.push(``);
    patrolReason.forEach(r => analysisLines.push(`   • ${r}`));
    analysisLines.push(``);
    
    // Final Decision
    analysisLines.push(`━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`);
    
    if (missionSafetyOutcome === "NO GO") {
      analysisLines.push(`DECISION: FLIGHT NOT RECOMMENDED`);
      analysisLines.push(`Postpone until VFR conditions confirmed.`);
    } else if (missionSafetyOutcome === "GO WITH CAUTION" && patrolOutcome === "REDUCED") {
      analysisLines.push(`DECISION: PROCEED WITH LIMITATIONS`);
      analysisLines.push(`Safe to fly but patrol results may be compromised.`);
    } else if (missionSafetyOutcome === "GO WITH CAUTION") {
      analysisLines.push(`DECISION: PROCEED WITH CAUTION`);
      analysisLines.push(`Monitor weather closely, be ready to abort.`);
    } else if (patrolOutcome === "REDUCED") {
      analysisLines.push(`DECISION: GO - REDUCED EFFECTIVENESS`);
      analysisLines.push(`Safe flight, but expect reduced patrol coverage.`);
    } else {
      analysisLines.push(`DECISION: GO`);
      analysisLines.push(`Conditions favorable for mission.`);
    }
    
    const fullAnalysis = analysisLines.join('\n');
    
    // Get visibility for stats
    let minVisibility = 9999;
    weatherData.forEach(a => {
      if (a.metar?.raw) {
        const visMatch = a.metar.raw.match(/\s(\d{4})\s/);
        if (visMatch) minVisibility = Math.min(minVisibility, parseInt(visMatch[1]));
        if (a.metar.raw.includes('9999')) minVisibility = Math.min(minVisibility, 9999);
      }
    });
    
    // Build TAF forecast data for FILTERED airports (based on selected routes)
    const tafForecastByAirport = [];
    
    // Process TAF for each airport - get ALL sections for display
    // If no TAF available, fall back to METAR data
    filteredWeatherData.forEach(airport => {
      if (!airport.taf?.raw && !airport.metar?.raw) return;
      
      // No TAF available — use METAR data instead
      if (!airport.taf?.raw) {
        const metar = airport.metar?.raw || '';
        let category = 'VFR';
        let wind = null;
        let ceiling = null;
        
        // Parse METAR wind
        const windMatch = metar.match(/(\d{3})(\d{2})(G(\d{2}))?KT/);
        if (windMatch) {
          wind = { dir: parseInt(windMatch[1]), speed: parseInt(windMatch[2]), gusts: windMatch[4] ? parseInt(windMatch[4]) : null };
        }
        
        // Parse METAR clouds
        const cloudMatch = metar.match(/(BKN|OVC|SCT|FEW)(\d{3})/);
        if (cloudMatch) {
          ceiling = { type: cloudMatch[1], alt: parseInt(cloudMatch[2]) * 100 };
          if (ceiling.alt < 500) category = 'LIFR';
          else if (ceiling.alt < 1000) category = 'IFR';
          else if (ceiling.alt < 3000) category = 'MVFR';
        }
        
        // Parse METAR visibility
        const visMatch = metar.match(/\s(\d{4})\s/);
        if (visMatch) {
          const vis = parseInt(visMatch[1]);
          if (vis < 1500) category = 'LIFR';
          else if (vis < 5000 && category !== 'LIFR') category = 'IFR';
          else if (vis < 8000 && category === 'VFR') category = 'MVFR';
        }
        
        // CAVOK override
        if (metar.includes('CAVOK')) {
          category = 'VFR';
          ceiling = null;
        }
        
        tafForecastByAirport.push({
          icao: airport.icao,
          name: airport.name,
          category,
          visibility: null,
          ceiling,
          wind,
          issues: [],
          sectionsCount: 0,
          hasMissionWindowData: false,
          source: 'METAR'
        });
        return;
      }
      
      // Get ALL TAF sections (includeAll = true) for display purposes
      const allTafSections = extractRelevantTafSections(airport.taf.raw, true);
      // Get only mission window sections for analysis
      const missionWindowSections = allTafSections.filter(s => s.inMissionWindow);
      
      if (allTafSections.length > 0) {
        // Get the most significant forecast for this airport (worst case from MISSION WINDOW sections)
        let worstCategory = 'VFR';
        let worstVisibility = 9999;
        let lowestCeiling = 99999;
        let maxGusts = 0;
        let representativeWind = null;
        let representativeCeiling = null;
        let issues = [];
        let hasMissionWindowData = missionWindowSections.length > 0;
        
        // Use mission window sections for worst case analysis (if available), otherwise fall back to all sections
        const sectionsToAnalyze = hasMissionWindowData ? missionWindowSections : allTafSections;
        
        sectionsToAnalyze.forEach(section => {
          const content = section.content;
          const timeLabel = section.end ? `${section.start}-${section.end}Z` : `FM${section.start}Z`;
          
          // Extract visibility
          const visMatch = content.match(/(\d{4})\s/);
          if (visMatch) {
            const vis = parseInt(visMatch[1]);
            if (vis < worstVisibility) worstVisibility = vis;
            if (vis < 5000) issues.push(`${section.type}: ${vis}m vis`);
          }
          
          // Extract ceiling/clouds
          const cloudMatch = content.match(/(BKN|OVC|SCT|FEW)(\d{3})/);
          if (cloudMatch) {
            const ceiling = parseInt(cloudMatch[2]) * 100;
            if (ceiling < lowestCeiling) {
              lowestCeiling = ceiling;
              representativeCeiling = { type: cloudMatch[1], alt: ceiling };
            }
            if (ceiling < 1500) issues.push(`${section.type}: ${cloudMatch[1]}@${ceiling}ft`);
          }
          
          // Extract wind with gusts
          const windMatch = content.match(/(\d{3})(\d{2})(G(\d{2}))?KT/);
          if (windMatch) {
            const gusts = windMatch[4] ? parseInt(windMatch[4]) : 0;
            if (gusts > maxGusts) {
              maxGusts = gusts;
              representativeWind = {
                dir: parseInt(windMatch[1]),
                speed: parseInt(windMatch[2]),
                gusts: gusts || null
              };
            } else if (!representativeWind) {
              representativeWind = {
                dir: parseInt(windMatch[1]),
                speed: parseInt(windMatch[2]),
                gusts: windMatch[4] ? parseInt(windMatch[4]) : null
              };
            }
          }
        });
        
        // Determine worst flight category
        if (worstVisibility < 1500 || lowestCeiling < 500) {
          worstCategory = 'LIFR';
        } else if (worstVisibility < 5000 || lowestCeiling < 1000) {
          worstCategory = 'IFR';
        } else if (worstVisibility < 8000 || lowestCeiling < 3000) {
          worstCategory = 'MVFR';
        }
        
        tafForecastByAirport.push({
          icao: airport.icao,
          name: airport.name,
          category: worstCategory,
          visibility: worstVisibility < 9999 ? worstVisibility : null,
          ceiling: representativeCeiling,
          wind: representativeWind,
          issues: issues.slice(0, 2), // Top 2 issues
          sectionsCount: allTafSections.length,
          hasMissionWindowData,
          rawTaf: airport.taf.raw // Include raw TAF for display
        });
      }
    });
    
    const tafSummary = departureData?.taf?.raw 
      ? departureData.taf.raw.split('\n').slice(0, 2).map(l => l.trim().substring(0, 50)).join(' ')
      : null;
    
    setWeatherPrediction({
      timeWindow: `${DEPARTURE_TIME} - ${ARRIVAL_TIME}`,
      conditions: missionSafetyOutcome,
      risk,
      summary: fullAnalysis,
      missionSafety: missionSafetyOutcome,
      missionSafetyReasons: missionSafetyReason.slice(0, 4), // Top 4 reasons
      patrolEffectiveness: patrolOutcome,
      patrolReasons: patrolReason.slice(0, 3), // Top 3 reasons
      avgVisibility: minVisibility >= 9999 ? "10km+" : `${minVisibility}m`,
      windSummary: ebosWind 
        ? `${ebosWind.direction}°/${ebosWind.speed}${ebosWind.gusts ? `G${ebosWind.gusts}` : ''}KT (XW: ${ebosCrosswind}KT)`
        : "N/A",
      crosswindSafe: ebosCrosswind <= CROSSWIND_LIMIT,
      cameraEffective: patrolOutcome === "EFFECTIVE",
      ebosCrosswind,
      flightLimitations,
      // Card data - TAF forecast for all airports along route
      tafForecastByAirport,
      tafSummary,
      tafHasIFR: tafForecastsIFR || tafForecastsLIFR,
      tafTimeWindow: `${missionStartTime.replace(':', '')}Z - ${missionEndTime.replace(':', '')}Z`,
      departureAirport: DEPARTURE_AIRPORT,
      missionStartTime,
      missionEndTime,
      // Data for PDF/HTML generation
      routeName: route.name || ftxDesignator,
      routeCoordinates: route.coordinates || [],
      routes: route.routes || [],  // Multiple routes array for multi-line display
      routeFiles: route.routeFiles || [],  // List of route file names
      selectedRouteIndices: selectedRouteIndices,  // Which routes are currently selected/visible
      routeAirportMap: routeAirportMap,  // Map of routeIndex -> array of airport ICAOs
      weatherData: weatherData, // Array of airport weather data (all airports)
      // Filtered weather data - airports from selected routes only
      filteredWeatherData: filteredWeatherData,
      // Route waypoint weather (including over water/sea)
      routeWaypointWeather: routeWaypointWeather
    });
  };

  if (!isActive) {
    return null;
  }

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Map className="w-4 h-4 text-indigo-600" />
          <h4 className="text-sm font-bold text-slate-700 uppercase tracking-wider">
            Flight Route {route.name || ftxDesignator}{route.description ? ` - ${route.description}` : ''}
          </h4>
        </div>
        
        {/* Mission Time Selection */}
        <div className="flex items-center gap-3 bg-slate-50 px-3 py-2 rounded-lg border border-slate-200">
          <Clock className="w-4 h-4 text-slate-500" />
          <span className="text-xs font-medium text-slate-600">Mission Window:</span>
          <select
            value={missionStartTime}
            onChange={(e) => setMissionStartTime(e.target.value)}
            className="text-xs bg-white border border-slate-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            {timeOptions.map(time => (
              <option key={`start-${time}`} value={time}>{time}Z</option>
            ))}
          </select>
          <span className="text-xs text-slate-500 dark:text-slate-400">to</span>
          <select
            value={missionEndTime}
            onChange={(e) => setMissionEndTime(e.target.value)}
            className="text-xs bg-white border border-slate-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            {timeOptions.map(time => (
              <option key={`end-${time}`} value={time}>{time}Z</option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center p-8 bg-slate-50 rounded-lg border border-slate-200">
          <Loader2 className="w-6 h-6 animate-spin text-indigo-600 mr-2" />
          <span className="text-slate-600">Loading route weather data...</span>
        </div>
      ) : error ? (
        <div className="p-4 bg-red-50 rounded-lg border border-red-200 text-red-700">
          <AlertTriangle className="w-4 h-4 inline mr-2" />
          {error}
        </div>
      ) : (
        <div className="space-y-4">
          {/* Route Selector - only show if multiple routes available */}
          {route.routes && route.routes.length > 1 && (
            <div className="flex items-center gap-3 bg-slate-50 px-4 py-3 rounded-lg border border-slate-200">
              <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Flight Lines:</span>
              <div className="flex flex-wrap gap-2">
                {route.routes.map((singleRoute, index) => {
                  const routeColors = ['#dc2626', '#2563eb', '#16a34a', '#9333ea', '#ea580c'];
                  const color = routeColors[index % routeColors.length];
                  const isSelected = selectedRouteIndices.includes(index);
                  return (
                    <button
                      key={`route-toggle-${index}`}
                      onClick={() => {
                        if (isSelected) {
                          // Don't allow deselecting all routes
                          if (selectedRouteIndices.length > 1) {
                            setSelectedRouteIndices(prev => prev.filter(i => i !== index));
                          }
                        } else {
                          setSelectedRouteIndices(prev => [...prev, index]);
                        }
                      }}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                        isSelected 
                          ? 'bg-white border-2 shadow-sm' 
                          : 'bg-slate-100 border border-slate-300 opacity-50 hover:opacity-75'
                      }`}
                      style={{ borderColor: isSelected ? color : undefined }}
                    >
                      <span 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: color }}
                      />
                      <span className={isSelected ? 'text-slate-700' : 'text-slate-500'}>
                        {singleRoute.name?.replace('.kml', '').replace('.kmz', '') || `Route ${index + 1}`}
                      </span>
                    </button>
                  );
                })}
              </div>
              <button
                onClick={() => setSelectedRouteIndices(route.routes.map((_, i) => i))}
                className="text-xs text-indigo-600 hover:text-indigo-800 font-medium ml-auto"
              >
                Show All
              </button>
            </div>
          )}
          
          {/* Map */}
          <div 
            ref={mapContainerRef}
            className="rounded-lg overflow-hidden border border-slate-200 relative" 
            style={{ height: '300px', zIndex: 0 }}
          >
            <MapContainer
              center={[50.7, 2.0]}
              zoom={8}
              style={{ height: '100%', width: '100%', zIndex: 0 }}
              scrollWheelZoom={false}
              whenReady={() => setMapReady(true)}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <FitBounds coordinates={route.coordinates} />
              <MapInvalidator />
              
              {/* Flight route polylines - multiple routes with different colors */}
              {route.routes && route.routes.length > 0 ? (
                // Multiple routes - only show selected ones
                route.routes.map((singleRoute, index) => {
                  // Skip if not selected
                  if (!selectedRouteIndices.includes(index)) return null;
                  
                  // Define colors for different routes
                  const routeColors = ['#dc2626', '#2563eb', '#16a34a', '#9333ea', '#ea580c'];
                  const color = routeColors[index % routeColors.length];
                  return (
                    <Polyline
                      key={`route-${index}`}
                      positions={singleRoute.coordinates}
                      color={color}
                      weight={3}
                      opacity={0.8}
                    />
                  );
                })
              ) : (
                // Single route (backward compatibility)
                <Polyline
                  positions={route.coordinates}
                  color="#dc2626"
                  weight={3}
                  opacity={0.8}
                />
              )}
              
              {/* Airport markers */}
              {airportWeather.map((airport) => (
                <Marker
                  key={airport.icao}
                  position={[airport.lat, airport.lon]}
                  icon={createAirportIcon(airport.metar?.flight_category)}
                >
                  <Popup>
                    <div className="text-sm">
                      <div className="font-bold">{airport.icao}</div>
                      <div className="text-slate-500">{airport.name}</div>
                      {airport.metar?.flight_category && (
                        <div className="mt-1">
                          <FlightCategoryBadge category={airport.metar.flight_category} />
                        </div>
                      )}
                    </div>
                  </Popup>
                </Marker>
              ))}
              
              {/* Weather waypoint markers */}
              {routeWaypointWeather?.waypoints?.filter(wp => wp.lat != null && wp.lon != null).map((wp, index) => (
                <Marker
                  key={`wx-${index}`}
                  position={[wp.lat, wp.lon]}
                  icon={createWeatherWaypointIcon(wp.weather_summary?.category, index, wp.is_over_water)}
                >
                  <Popup>
                    <div className="text-sm min-w-[140px]">
                      <div className="font-bold text-slate-700">{wp.location_name || `Waypoint ${wp.waypoint_number || index + 1}`}</div>
                      {wp.country && (
                        <div className="text-xs text-slate-500">{wp.country}</div>
                      )}
                      {wp.show_coordinates && (
                        <div className="text-[10px] text-slate-500 font-mono mt-0.5">
                          {formatCoordsDMS(wp.lat, wp.lon)}
                        </div>
                      )}
                      <div className="mt-1.5 flex items-center gap-2">
                        <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${
                          wp.weather_summary?.category === 'VFR' ? 'bg-emerald-100 text-emerald-700' :
                          wp.weather_summary?.category === 'MVFR' ? 'bg-amber-100 text-amber-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {wp.weather_summary?.category || 'N/A'}
                        </span>
                        {wp.weather_summary?.caution && (
                          <span className="px-1.5 py-0.5 rounded text-xs font-bold bg-amber-100 text-amber-700">
                            CAUTION
                          </span>
                        )}
                      </div>
                      {wp.weather_summary && (
                        <div className="mt-1.5 text-xs text-slate-600 space-y-0.5">
                          <div>Wind: {wp.weather_summary.wind_kt?.toFixed(0) || 0}kt @ {wp.weather_summary.wind_dir || 0}°</div>
                          <div>Vis: {wp.weather_summary.visibility_km?.toFixed(1) || 10}km</div>
                          <div>Clouds: {wp.weather_summary.clouds_pct || 0}%</div>
                          {wp.weather_summary.description && <div className="italic">{wp.weather_summary.description}</div>}
                          {wp.weather_summary.caution_reasons?.map((reason, i) => (
                            <div key={i} className="text-amber-600 text-[10px]">⚠ {reason}</div>
                          ))}
                        </div>
                      )}
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>

          {/* Weather Prediction - 2x2 Card Grid */}
          {weatherPrediction && showPrediction && (
            <div className={`p-3 sm:p-4 rounded-lg border relative ${
              weatherPrediction.risk === 'HIGH' ? 'bg-red-50 border-red-200' :
              weatherPrediction.risk === 'MODERATE' ? 'bg-amber-50 border-amber-200' :
              'bg-emerald-50 border-emerald-200'
            }`}>
              {/* Close button */}
              <button
                onClick={() => setShowPrediction(false)}
                className={`absolute top-2 right-2 p-1 rounded-full hover:bg-black/10 transition-colors z-10 ${
                  weatherPrediction.risk === 'HIGH' ? 'text-red-600 hover:text-red-800' :
                  weatherPrediction.risk === 'MODERATE' ? 'text-amber-600 hover:text-amber-800' :
                  'text-emerald-600 hover:text-emerald-800'
                }`}
                title="Remove prediction from briefing"
              >
                <X className="w-4 h-4" />
              </button>
              
              {/* Header */}
              <div className="flex items-center justify-between mb-4 pr-6">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span className="font-semibold text-sm sm:text-base">Weather Analysis by AI</span>
                  <span className="text-xs text-slate-500">{weatherPrediction.timeWindow}</span>
                  <button
                    onClick={() => setShowAiHelpDialog(true)}
                    className="p-0.5 rounded-full hover:bg-white/30"
                    title="How is this generated?"
                  >
                    <HelpCircle className="w-3.5 h-3.5" />
                  </button>
                </div>
                
                {/* Raw Analysis Actions */}
                <div className="flex items-center gap-1">
                  <button
                    onClick={handleCopyRawAnalysis}
                    className="p-1.5 rounded hover:bg-white/50 text-slate-500 hover:text-slate-700 transition-colors"
                    title="Copy raw analysis to clipboard"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                  <button
                    onClick={handleDownloadRawAnalysis}
                    className="p-1.5 rounded hover:bg-white/50 text-slate-500 hover:text-slate-700 transition-colors"
                    title="Download raw analysis as TXT"
                  >
                    <FileText className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              {/* 2x2 Card Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                
                {/* Card 1: Airport Weather Overview */}
                <div className="bg-white/70 dark:bg-slate-800/60 rounded-lg p-3 sm:p-4 border border-slate-200 dark:border-slate-600/50">
                  <div className="flex items-center gap-2 mb-3">
                    <MapPin className="w-4 h-4 text-cyan-600 dark:text-cyan-400" />
                    <h6 className="text-xs sm:text-sm font-bold text-slate-700 dark:text-slate-300 uppercase">Airport Weather</h6>
                    <span className="text-[10px] sm:text-xs text-slate-500">({weatherPrediction.tafTimeWindow})</span>
                  </div>
                  
                  {/* Airport details */}
                  <div className="space-y-2">
                    {weatherPrediction.tafForecastByAirport?.map((apt, i) => (
                      <div key={i} className="flex items-center justify-between gap-2 text-[11px] sm:text-xs">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <span className={`px-2 py-1 rounded font-bold shrink-0 ${
                            apt.category === 'VFR' ? 'bg-emerald-100 dark:bg-emerald-900/60 text-emerald-700 dark:text-emerald-400 border border-emerald-300 dark:border-emerald-600/50' :
                            apt.category === 'MVFR' ? 'bg-amber-100 dark:bg-amber-900/60 text-amber-700 dark:text-amber-400 border border-amber-300 dark:border-amber-600/50' :
                            'bg-red-100 dark:bg-red-900/60 text-red-700 dark:text-red-400 border border-red-300 dark:border-red-600/50'
                          }`}>{apt.icao}</span>
                          <span className="text-slate-500 dark:text-slate-400 truncate">{apt.name}</span>
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold shrink-0 ${
                            apt.category === 'VFR' ? 'bg-emerald-500 text-white' :
                            apt.category === 'MVFR' ? 'bg-amber-500 text-white' :
                            'bg-red-500 text-white'
                          }`}>{apt.category}</span>
                        </div>
                        <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400 shrink-0 text-xs">
                          {apt.ceiling ? (
                            <span className={apt.ceiling.alt < 1500 ? 'text-red-600 dark:text-red-400' : ''}>
                              {apt.ceiling.type}@{apt.ceiling.alt}ft
                            </span>
                          ) : (
                            <span className="text-emerald-600 dark:text-emerald-400 font-medium">CAVOK</span>
                          )}
                          {apt.wind && (
                            <span>{apt.wind.dir}°/{apt.wind.speed}kt</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {weatherPrediction.tafHasIFR && (
                    <div className="mt-3 text-xs text-red-600 dark:text-red-400 font-medium bg-red-100 dark:bg-red-900/40 border border-red-300 dark:border-red-600/50 px-3 py-2 rounded">
                      ⚠ IFR conditions forecast during mission window
                    </div>
                  )}
                </div>
                
                {/* Card 2: Route/Enroute Weather */}
                <div className="bg-white/70 dark:bg-slate-800/60 rounded-lg p-3 sm:p-4 border border-slate-200 dark:border-slate-600/50">
                  <div className="flex items-center gap-2 mb-3">
                    <Navigation className="w-4 h-4 text-cyan-600 dark:text-cyan-400" />
                    <h6 className="text-xs sm:text-sm font-bold text-slate-700 dark:text-slate-300 uppercase">Enroute Weather</h6>
                  </div>
                  
                  {routeWaypointWeather?.waypoints?.length > 0 ? (
                    <>
                      {/* Data Source Info */}
                      <div className="mb-3 px-2 py-1.5 bg-slate-100 dark:bg-slate-700/50 rounded text-[10px] sm:text-xs text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-600/30">
                        <span className="font-medium">Source:</span> OpenWeatherMap API • {routeWaypointWeather.waypoints.length} waypoints 
                        {selectedRouteIndices.length > 0 && ftxData?.routes?.length > 1 && (
                          <span className="ml-1">• Routes: {selectedRouteIndices.map(i => i + 1).join(', ')}/{ftxData.routes.length}</span>
                        )}
                      </div>
                      
                      {/* Waypoint weather cards - show actual conditions */}
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {routeWaypointWeather.waypoints.slice(0, 9).map((wp, i) => {
                          const ws = wp.weather_summary;
                          const wid = ws?.weather_id || 800;
                          const hasRain = wid >= 200 && wid < 600;
                          const hasSnow = wid >= 600 && wid < 700;
                          const hasFog = wid >= 700 && wid < 800;
                          const tempC = ws?.temp_c != null ? Math.round(ws.temp_c) : null;
                          const icingRisk = tempC != null && tempC <= 2 && ws?.humidity_pct > 80;
                          const ceiling = ws?.cloud_ceiling_ft;
                          const ceilingLow = ceiling != null && ceiling < 2000;
                          const ceilingCaution = ceiling != null && ceiling >= 2000 && ceiling < 2500;
                          
                          return (
                            <div key={i} className="bg-slate-100 dark:bg-slate-700/40 rounded px-2.5 py-2 border border-slate-200 dark:border-slate-600/30" title={formatCoordsDMS(wp.lat, wp.lon)}>
                              <div className="font-semibold text-slate-700 dark:text-slate-300 truncate text-xs sm:text-sm mb-1.5">
                                <span className="text-cyan-600 dark:text-cyan-400">{i + 1}.</span> {wp.location_name || `Wp ${i + 1}`}
                              </div>
                              <div className="text-[10px] sm:text-xs text-slate-600 dark:text-slate-400 capitalize mb-1">
                                {ws?.description || 'N/A'}
                              </div>
                              <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] sm:text-xs text-slate-500 dark:text-slate-400">
                                <span title="Wind">{ws?.wind_dir != null ? `${Math.round(ws.wind_dir)}°/` : ''}{ws?.wind_kt?.toFixed(0) || 0}kt</span>
                                <span title="Visibility">vis {ws?.visibility_km || '?'}km</span>
                                {tempC != null && <span title="Temperature">{tempC}°C</span>}
                              </div>
                              {/* Cloud ceiling - key for mission altitude */}
                              {ceiling != null && (
                                <div className={`mt-1 text-[10px] sm:text-xs font-medium ${
                                  ceilingLow ? 'text-red-600 dark:text-red-400' : 
                                  ceilingCaution ? 'text-amber-600 dark:text-amber-400' : 
                                  'text-slate-500 dark:text-slate-400'
                                }`} title="Estimated cloud base (temp/dewpoint spread)">
                                  ceiling ~{ceiling}ft
                                </div>
                              )}
                              {ws?.clouds_pct != null && ws.clouds_pct <= 10 && (
                                <div className="mt-1 text-[10px] sm:text-xs text-emerald-600 dark:text-emerald-400 font-medium">
                                  clear sky
                                </div>
                              )}
                              {/* Alert flags */}
                              <div className="flex flex-wrap gap-1 mt-1">
                                {ceilingLow && <span className="text-[9px] px-1 py-0.5 rounded bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 font-medium">Below 2000ft</span>}
                                {ceilingCaution && <span className="text-[9px] px-1 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 font-medium">Below 2500ft</span>}
                                {hasRain && <span className="text-[9px] px-1 py-0.5 rounded bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 font-medium">Rain</span>}
                                {hasSnow && <span className="text-[9px] px-1 py-0.5 rounded bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 font-medium">Snow</span>}
                                {hasFog && <span className="text-[9px] px-1 py-0.5 rounded bg-gray-200 dark:bg-gray-700/40 text-gray-700 dark:text-gray-300 font-medium">Fog/Mist</span>}
                                {icingRisk && <span className="text-[9px] px-1 py-0.5 rounded bg-cyan-100 dark:bg-cyan-900/40 text-cyan-700 dark:text-cyan-300 font-medium">Icing risk</span>}
                                {ws?.visibility_km != null && ws.visibility_km < 5 && <span className="text-[9px] px-1 py-0.5 rounded bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 font-medium">Low vis</span>}
                                {ws?.wind_kt > 25 && <span className="text-[9px] px-1 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 font-medium">Strong wind</span>}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      
                      {/* Route summary */}
                      <div className="mt-3 text-xs text-slate-500 dark:text-slate-400 flex flex-wrap gap-x-4 gap-y-1">
                        <span>Max wind: <b className="text-slate-700 dark:text-slate-300">{routeWaypointWeather.route_assessment?.max_wind_kt?.toFixed(0) || 0}kt</b></span>
                        <span>Min vis: <b className="text-slate-700 dark:text-slate-300">{routeWaypointWeather.route_assessment?.min_visibility_km?.toFixed(0) || 10}km</b></span>
                        {routeWaypointWeather.waypoints.some(w => w.weather_summary?.cloud_ceiling_ft != null) && (
                          <span>Lowest ceiling: <b className={
                            Math.min(...routeWaypointWeather.waypoints.filter(w => w.weather_summary?.cloud_ceiling_ft != null).map(w => w.weather_summary.cloud_ceiling_ft)) < 2000
                              ? 'text-red-600 dark:text-red-400'
                              : Math.min(...routeWaypointWeather.waypoints.filter(w => w.weather_summary?.cloud_ceiling_ft != null).map(w => w.weather_summary.cloud_ceiling_ft)) < 2500
                              ? 'text-amber-600 dark:text-amber-400'
                              : 'text-slate-700 dark:text-slate-300'
                          }>~{Math.min(...routeWaypointWeather.waypoints.filter(w => w.weather_summary?.cloud_ceiling_ft != null).map(w => w.weather_summary.cloud_ceiling_ft))}ft</b></span>
                        )}
                        {routeWaypointWeather.waypoints.some(w => w.weather_summary?.temp_c != null) && (
                          <span>Temp: <b className="text-slate-700 dark:text-slate-300">
                            {Math.round(Math.min(...routeWaypointWeather.waypoints.filter(w => w.weather_summary?.temp_c != null).map(w => w.weather_summary.temp_c)))}° to {Math.round(Math.max(...routeWaypointWeather.waypoints.filter(w => w.weather_summary?.temp_c != null).map(w => w.weather_summary.temp_c)))}°C
                          </b></span>
                        )}
                      </div>
                      <div className="mt-1 text-[10px] text-slate-400 dark:text-slate-500 italic">
                        Ceiling estimated from temp/dewpoint spread • mission alt 2500ft (min 2000ft)
                      </div>
                    </>
                  ) : (
                    <p className="text-xs text-slate-400 italic">Route weather not available</p>
                  )}
                </div>
                
                {/* Card 3: Flight Limitations */}
                <div className="bg-white/70 dark:bg-slate-800/60 rounded-lg p-3 sm:p-4 border border-slate-200 dark:border-slate-600/50">
                  <h6 className="text-xs sm:text-sm font-bold text-slate-700 dark:text-slate-300 uppercase mb-3">Flight Limitations</h6>
                  <div className="grid grid-cols-2 gap-2 text-xs sm:text-sm">
                    <div className={`flex items-center gap-2 px-2 py-1.5 rounded border ${weatherPrediction.flightLimitations?.poorVisibility ? 'bg-red-100 dark:bg-red-900/40 border-red-300 dark:border-red-600/50' : 'bg-slate-100 dark:bg-slate-700/40 border-slate-200 dark:border-slate-600/30'}`}>
                      <span className={weatherPrediction.flightLimitations?.poorVisibility ? 'text-red-600 dark:text-red-400' : 'text-slate-500'}>
                        {weatherPrediction.flightLimitations?.poorVisibility ? '☑' : '☐'}
                      </span>
                      <span className={weatherPrediction.flightLimitations?.poorVisibility ? 'text-red-600 dark:text-red-400 font-medium' : 'text-slate-500'}>Low visibility</span>
                    </div>
                    <div className={`flex items-center gap-2 px-2 py-1.5 rounded border ${weatherPrediction.flightLimitations?.lowClouds ? 'bg-red-100 dark:bg-red-900/40 border-red-300 dark:border-red-600/50' : 'bg-slate-100 dark:bg-slate-700/40 border-slate-200 dark:border-slate-600/30'}`}>
                      <span className={weatherPrediction.flightLimitations?.lowClouds ? 'text-red-600 dark:text-red-400' : 'text-slate-500'}>
                        {weatherPrediction.flightLimitations?.lowClouds ? '☑' : '☐'}
                      </span>
                      <span className={weatherPrediction.flightLimitations?.lowClouds ? 'text-red-600 dark:text-red-400 font-medium' : 'text-slate-500'}>Low clouds</span>
                    </div>
                    <div className={`flex items-center gap-2 px-2 py-1.5 rounded border ${weatherPrediction.flightLimitations?.crosswindExceeds20 ? 'bg-amber-100 dark:bg-amber-900/40 border-amber-300 dark:border-amber-600/50' : 'bg-slate-100 dark:bg-slate-700/40 border-slate-200 dark:border-slate-600/30'}`}>
                      <span className={weatherPrediction.flightLimitations?.crosswindExceeds20 ? 'text-amber-600 dark:text-amber-400' : 'text-slate-500'}>
                        {weatherPrediction.flightLimitations?.crosswindExceeds20 ? '☑' : '☐'}
                      </span>
                      <span className={weatherPrediction.flightLimitations?.crosswindExceeds20 ? 'text-amber-600 dark:text-amber-400 font-medium' : 'text-slate-500'}>XW &gt;20kt</span>
                    </div>
                    <div className={`flex items-center gap-2 px-2 py-1.5 rounded border ${weatherPrediction.flightLimitations?.thunderstormCB ? 'bg-amber-100 dark:bg-amber-900/40 border-amber-300 dark:border-amber-600/50' : 'bg-slate-100 dark:bg-slate-700/40 border-slate-200 dark:border-slate-600/30'}`}>
                      <span className={weatherPrediction.flightLimitations?.thunderstormCB ? 'text-amber-600 dark:text-amber-400' : 'text-slate-500'}>
                        {weatherPrediction.flightLimitations?.thunderstormCB ? '☑' : '☐'}
                      </span>
                      <span className={weatherPrediction.flightLimitations?.thunderstormCB ? 'text-amber-600 dark:text-amber-400 font-medium' : 'text-slate-500'}>TS/CB</span>
                    </div>
                    <div className={`flex items-center gap-2 px-2 py-1.5 rounded border ${weatherPrediction.flightLimitations?.knownIcingConditions ? 'bg-red-100 dark:bg-red-900/40 border-red-300 dark:border-red-600/50' : 'bg-slate-100 dark:bg-slate-700/40 border-slate-200 dark:border-slate-600/30'}`}>
                      <span className={weatherPrediction.flightLimitations?.knownIcingConditions ? 'text-red-600 dark:text-red-400' : 'text-slate-500'}>
                        {weatherPrediction.flightLimitations?.knownIcingConditions ? '☑' : '☐'}
                      </span>
                      <span className={weatherPrediction.flightLimitations?.knownIcingConditions ? 'text-red-600 dark:text-red-400 font-medium' : 'text-slate-500'}>Icing</span>
                    </div>
                    <div className={`flex items-center gap-2 px-2 py-1.5 rounded border ${weatherPrediction.tafHasIFR ? 'bg-red-100 dark:bg-red-900/40 border-red-300 dark:border-red-600/50' : 'bg-slate-100 dark:bg-slate-700/40 border-slate-200 dark:border-slate-600/30'}`}>
                      <span className={weatherPrediction.tafHasIFR ? 'text-red-600 dark:text-red-400' : 'text-slate-500'}>
                        {weatherPrediction.tafHasIFR ? '☑' : '☐'}
                      </span>
                      <span className={weatherPrediction.tafHasIFR ? 'text-red-600 dark:text-red-400 font-medium' : 'text-slate-500'}>IFR expected</span>
                    </div>
                  </div>
                </div>
                
                {/* Card 4: Combined Decision */}
                <div className={`rounded-lg p-3 sm:p-4 border-2 ${
                  weatherPrediction.missionSafety === 'GO' ? 'bg-emerald-100 dark:bg-emerald-900/40 border-emerald-400 dark:border-emerald-500/60' :
                  weatherPrediction.missionSafety === 'GO WITH CAUTION' ? 'bg-amber-100 dark:bg-amber-900/40 border-amber-400 dark:border-amber-500/60' :
                  'bg-red-100 dark:bg-red-900/40 border-red-400 dark:border-red-500/60'
                }`}>
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <div className="flex items-center gap-2">
                      <span className={`text-2xl ${
                        weatherPrediction.missionSafety === 'GO' ? 'text-emerald-600 dark:text-emerald-400' :
                        weatherPrediction.missionSafety === 'GO WITH CAUTION' ? 'text-amber-600 dark:text-amber-400' :
                        'text-red-600 dark:text-red-400'
                      }`}>
                        {weatherPrediction.missionSafety === 'GO' ? '✓' : 
                         weatherPrediction.missionSafety === 'GO WITH CAUTION' ? '⚠' : '✗'}
                      </span>
                      <div>
                        <div className={`text-base sm:text-lg font-bold ${
                          weatherPrediction.missionSafety === 'GO' ? 'text-emerald-600 dark:text-emerald-400' :
                          weatherPrediction.missionSafety === 'GO WITH CAUTION' ? 'text-amber-600 dark:text-amber-400' :
                          'text-red-600 dark:text-red-400'
                        }`}>{weatherPrediction.missionSafety}</div>
                        <div className="text-[10px] sm:text-xs text-slate-500 dark:text-slate-400 uppercase">Mission Safety</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`text-base sm:text-lg font-bold ${
                        weatherPrediction.patrolEffectiveness === 'EFFECTIVE' ? 'text-emerald-600 dark:text-emerald-400' :
                        weatherPrediction.patrolEffectiveness === 'REDUCED' ? 'text-amber-600 dark:text-amber-400' :
                        'text-red-600 dark:text-red-400'
                      }`}>{weatherPrediction.patrolEffectiveness}</div>
                      <div className="text-[10px] sm:text-xs text-slate-500 dark:text-slate-400 uppercase">Patrol Outcome</div>
                    </div>
                  </div>
                  
                  {/* Mission Safety Reasons */}
                  <div className="text-xs sm:text-sm text-slate-700 dark:text-slate-300 space-y-1 mb-3">
                    {weatherPrediction.missionSafetyReasons?.slice(0, 3).map((r, i) => (
                      <div key={i}>• {r}</div>
                    ))}
                  </div>
                  
                  {/* Patrol Outcome Justification - Camera Impact */}
                  {weatherPrediction.patrolEffectiveness !== 'EFFECTIVE' && (
                    <div className={`mt-2 pt-2 border-t ${
                      weatherPrediction.patrolEffectiveness === 'REDUCED' ? 'border-amber-600/40' : 'border-red-600/40'
                    }`}>
                      <div className="text-[10px] sm:text-xs text-slate-500 dark:text-slate-400 uppercase mb-1">MX-15 Sensor Impact</div>
                      <div className={`text-xs sm:text-sm ${
                        weatherPrediction.patrolEffectiveness === 'REDUCED' ? 'text-amber-700 dark:text-amber-300' : 'text-red-700 dark:text-red-300'
                      }`}>
                        {weatherPrediction.patrolReasons?.length > 0 ? (
                          weatherPrediction.patrolReasons.slice(0, 3).map((r, i) => (
                            <div key={i}>• {r}</div>
                          ))
                        ) : (
                          <>
                            {weatherPrediction.flightLimitations?.lowClouds && (
                              <div>• Low ceiling obstructs camera ground visibility</div>
                            )}
                            {weatherPrediction.flightLimitations?.poorVisibility && (
                              <div>• Fog/mist severely reduces imaging quality</div>
                            )}
                            {weatherPrediction.tafHasIFR && (
                              <div>• IFR conditions prevent effective surveillance</div>
                            )}
                            {!weatherPrediction.flightLimitations?.lowClouds && 
                             !weatherPrediction.flightLimitations?.poorVisibility && 
                             !weatherPrediction.tafHasIFR && (
                              <div>• Weather conditions degrade camera effectiveness</div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Card 5: Final Conclusion - Full Width */}
                <div className={`sm:col-span-2 rounded-lg p-4 border ${
                  weatherPrediction.missionSafety === 'GO' ? 'bg-emerald-100 dark:bg-emerald-900/40 border-emerald-300 dark:border-emerald-600/50' :
                  weatherPrediction.missionSafety === 'GO WITH CAUTION' ? 'bg-amber-100 dark:bg-amber-900/40 border-amber-300 dark:border-amber-600/50' :
                  'bg-red-100 dark:bg-red-900/40 border-red-300 dark:border-red-600/50'
                }`}>
                  <h6 className="text-xs sm:text-sm font-bold text-slate-700 dark:text-slate-300 uppercase mb-2">Briefing Conclusion</h6>
                  <p className="text-xs sm:text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
                    {weatherPrediction.missionSafety === 'NO GO' ? (
                      <>
                        <b>Flight not recommended.</b> Combined analysis of {weatherPrediction.tafForecastByAirport?.length || 0} airport TAF forecasts 
                        {routeWaypointWeather?.waypoints?.length > 0 && ` and ${routeWaypointWeather.waypoints.length} enroute waypoints`} indicates 
                        conditions unsuitable for VFR patrol.
                        {weatherPrediction.tafHasIFR && ' Official TAF shows IFR at key airports.'}
                        {routeWaypointWeather?.route_assessment?.ifr_waypoints > 0 && 
                          ` ${routeWaypointWeather.route_assessment.ifr_waypoints} route waypoints report IFR.`}
                        {routeWaypointWeather?.route_assessment?.over_water_waypoints > 0 && 
                          ` Enroute segments (${routeWaypointWeather.waypoints.length} waypoints) require monitoring.`}
                        {' '}IFR capability reserved for abort/RTB only. <b>Recommend postponing until conditions improve.</b>
                      </>
                    ) : weatherPrediction.missionSafety === 'GO WITH CAUTION' ? (
                      <>
                        <b>Proceed with caution.</b> Weather conditions are marginal but acceptable.
                        {routeWaypointWeather?.route_assessment?.mvfr_waypoints > 0 && 
                          ` ${routeWaypointWeather.route_assessment.mvfr_waypoints} waypoints show MVFR.`}
                        {routeWaypointWeather?.route_assessment?.over_water_waypoints > 0 && 
                          ` Monitor enroute weather (${routeWaypointWeather.waypoints.length} waypoints).`}
                        {' '}Maintain situational awareness. Be prepared to abort if conditions deteriorate.
                        {' '}Alternates: {weatherPrediction.tafForecastByAirport?.filter(a => a.category === 'VFR').map(a => a.icao).join(', ') || 'Check availability'}.
                      </>
                    ) : (
                      <>
                        <b>Flight approved.</b> VFR conditions favorable at all {weatherPrediction.tafForecastByAirport?.length || 0} airports 
                        {routeWaypointWeather?.waypoints?.length > 0 && ` and ${routeWaypointWeather.route_assessment?.vfr_waypoints || 0}/${routeWaypointWeather.waypoints.length} enroute waypoints`}.
                        {routeWaypointWeather?.route_assessment?.over_water_waypoints > 0 && 
                          ` Enroute weather acceptable (${routeWaypointWeather.waypoints.length} waypoints monitored).`}
                        {' '}Standard ops apply. Patrol effectiveness expected optimal.
                      </>
                    )}
                  </p>
                  <div className="mt-2 pt-1.5 border-t border-slate-200/50 text-[8px] text-slate-500 dark:text-slate-400">
                    Sources: aviationweather.gov (METAR/TAF) • OpenWeatherMap (enroute) • {new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} UTC
                  </div>
                </div>
                
              </div>
            </div>
          )}

          {/* AI Weather Briefing from Weather Briefing tab - always visible when available */}
          {aiWeatherBriefing && (
            <div className="p-4 rounded-lg border bg-purple-50 border-purple-200">
              <p className="text-xs font-bold text-purple-600 mb-2 uppercase">AI Weather Briefing</p>
              <div className="text-sm text-slate-700 whitespace-pre-wrap bg-white p-3 rounded border border-purple-100">
                {aiWeatherBriefing}
              </div>
            </div>
          )}

          {/* Windy Maps - Interactive iframe panel */}
          <WindyMapsPanel 
            lat={routeCenter.lat} 
            lon={routeCenter.lon} 
            onVisibilityChange={setWindyMapsVisible}
            onSnapshotCapture={(snapshot) => {
              if (onWindySnapshotChange) {
                onWindySnapshotChange(snapshot);
              }
            }}
            savedSnapshots={savedWindySnapshots}
          />

          {/* Airport Weather Details */}
          <div className="bg-slate-50 rounded-lg border border-slate-200 p-4">
            <h5 className="text-xs font-bold text-slate-600 uppercase mb-3">WX Info Airports on Routing</h5>
            <div className="space-y-3">
              {visibleAirportWeather.map((airport) => (
                <div key={airport.icao} className="p-3 bg-white rounded-lg border border-slate-100 relative">
                  <button
                    onClick={() => handleHideRouteAirport(airport.icao)}
                    className="absolute top-2 right-2 p-1 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                    title={`Remove ${airport.icao} from briefing`}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                  <div className="flex items-center justify-between mb-2 pr-6">
                    <div className="flex items-center gap-2">
                      <Plane className="w-4 h-4 text-indigo-600" />
                      <span className="font-bold text-sm">{airport.icao}</span>
                      <span className="text-xs text-slate-500">{airport.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {(airport.metar?.raw || airport.taf?.raw) && (
                        <button
                          onClick={() => handleCopyWeather(airport)}
                          className="p-1 rounded hover:bg-slate-100 transition-colors"
                          title="Copy METAR/TAF to clipboard"
                        >
                          {copiedAirport === airport.icao ? (
                            <Check className="w-4 h-4 text-green-600" />
                          ) : (
                            <Copy className="w-4 h-4 text-slate-400 hover:text-slate-600" />
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {/* Current Flying Conditions from METAR */}
                  {airport.metar?.flight_category && (
                    <div className={`mb-2 p-2 rounded-md border ${
                      airport.metar.flight_category === 'VFR' ? 'bg-emerald-50 border-emerald-200' :
                      airport.metar.flight_category === 'MVFR' ? 'bg-blue-50 border-blue-200' :
                      airport.metar.flight_category === 'IFR' ? 'bg-red-50 border-red-200' :
                      airport.metar.flight_category === 'LIFR' ? 'bg-purple-50 border-purple-200' :
                      'bg-slate-50 border-slate-200'
                    }`}>
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] uppercase font-semibold text-slate-500">Current Flying Conditions</span>
                        <FlightCategoryBadge category={airport.metar.flight_category} />
                      </div>
                    </div>
                  )}
                  
                  {airport.metar?.raw && (
                    <div className="mb-2">
                      <p className="text-xs text-slate-500 uppercase">METAR</p>
                      <p className="font-mono text-xs text-slate-700">{airport.metar.raw}</p>
                    </div>
                  )}
                  {airport.taf?.raw && (
                    <div>
                      <p className="text-xs text-slate-500 uppercase">TAF</p>
                      <p className="font-mono text-xs text-slate-700">{airport.taf.raw}</p>
                    </div>
                  )}
                  {!airport.metar?.raw && !airport.taf?.raw && (
                    <p className="text-xs text-slate-400 italic">No weather data available</p>
                  )}
                </div>
              ))}
              {visibleAirportWeather.length === 0 && (
                <p className="text-xs text-slate-400 italic text-center py-2">All airports have been removed</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* AI Help Dialog */}
      <Dialog open={showAiHelpDialog} onOpenChange={setShowAiHelpDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-purple-600" />
              How is this data generated?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm text-slate-700">
            <div>
              <h4 className="font-semibold text-slate-800 mb-1">Data Source</h4>
              <p>The AI explanation is generated by <strong>OpenAI GPT-5.2</strong> via the /api/weather/explain endpoint.</p>
            </div>
            
            <div>
              <h4 className="font-semibold text-slate-800 mb-1">Input Data</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>METAR (current weather observation)</li>
                <li>TAF (terminal aerodrome forecast)</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-slate-800 mb-1">What the AI Analyzes</h4>
              <p className="mb-2"><strong>For METAR:</strong></p>
              <ul className="list-disc list-inside space-y-1 ml-2 mb-2">
                <li>Current flight category (VFR/MVFR/IFR/LIFR)</li>
                <li>Visibility and cloud conditions</li>
                <li>Wind (direction, speed, gusts)</li>
                <li>Any significant weather phenomena</li>
                <li>Temperature/dewpoint spread</li>
              </ul>
              <p className="mb-2"><strong>For TAF:</strong></p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Expected changes over the forecast period</li>
                <li>Timing of weather changes</li>
                <li>Any temporary conditions (TEMPO) or probability forecasts (PROB)</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-slate-800 mb-1">Flow</h4>
              <ol className="list-decimal list-inside space-y-1 ml-2">
                <li>User enters ICAO code and clicks &quot;Get Weather&quot;</li>
                <li>METAR/TAF data is fetched from aviationweather.gov API</li>
                <li>User clicks &quot;Generate Briefing&quot; button</li>
                <li>Frontend sends METAR/TAF to /api/weather/explain</li>
                <li>Backend sends data to GPT-5.2 with a pilot-focused prompt</li>
                <li>AI returns plain English explanation</li>
                <li>Displayed under &quot;AI Weather Briefing&quot;</li>
              </ol>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FlightRouteWeather;
